import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiFetch, getLatestDDRData } from '../api/api';
import { exportDDRToExcel } from '../utils/export2';
import { formatDate } from '../utils/utils';
import { isSameDate, isFieldLookup } from '../utils/formatters';
import { formatFieldValue } from '../utils/formatters';
import { getGlobalUnitSettings } from '../utils/globalUnits';
import useDDRPDFExport from '../hooks/useDDRPDFExport';
import { 
    isCalculatedField, 
    evaluateCalculation, 
    recalculateFields,
    getDependentFields
} from '../utils/calculatedFields';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataForm } from '../components/forms';
import LookupField from '../components/forms/LookupField';
import ContextMenu from '../components/ContextMenu';
import './DDR_v2.css';
import { customCalculations } from '../utils/customCalculations';

// Generate global styles from layout configuration
const generateGlobalStyles = (layoutConfig) => {
    if (!layoutConfig?.globalStyles) return {};
    
    const styles = layoutConfig.globalStyles;
    return {
        fontFamily: styles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
        fontSize: styles.fontSize || '14px',
        color: styles.textColor || '#1e293b',
        backgroundColor: styles.backgroundColor || '#ffffff',
        '--section-spacing': styles.sectionSpacing || '20px',
        '--section-padding': styles.sectionPadding || '16px',
        '--group-spacing': styles.groupSpacing || '16px',
        '--group-padding': styles.groupPadding || '12px',
        '--border-color': styles.borderColor || '#e5e7eb',
        '--border-radius': styles.borderRadius || '8px',
        '--section-header-background': styles.sectionHeaderBackgroundColor || '#f8fafc',
        '--section-header-font-size': styles.sectionHeaderFontSize || '16px',
        '--section-header-font-weight': styles.sectionHeaderFontWeight || '600',
        '--section-header-text-color': styles.sectionHeaderTextColor || '#1e293b',
        '--report-container-padding': styles.reportContainerPadding || '20px',
        '--report-container-spacing': styles.reportContainerSpacing || '0px'
    };
};

// Generate custom CSS from layout configuration
const generateCustomCSS = (layoutConfig) => {
    if (!layoutConfig) return '';
    
    let css = '';
    
    // Add global styles as CSS variables and base styles
    if (layoutConfig.globalStyles) {
        css += `
            .ddr-v2-container {
                --section-spacing: ${layoutConfig.globalStyles.sectionSpacing || '20px'};
                --section-padding: ${layoutConfig.globalStyles.sectionPadding || '16px'};
                --group-spacing: ${layoutConfig.globalStyles.groupSpacing || '16px'};
                --group-padding: ${layoutConfig.globalStyles.groupPadding || '12px'};
                --border-color: ${layoutConfig.globalStyles.borderColor || '#e5e7eb'};
                --border-radius: ${layoutConfig.globalStyles.borderRadius || '8px'};
                --section-header-background: ${layoutConfig.globalStyles.sectionHeaderBackgroundColor || '#f8fafc'};
                --section-header-font-size: ${layoutConfig.globalStyles.sectionHeaderFontSize || '16px'};
                --section-header-font-weight: ${layoutConfig.globalStyles.sectionHeaderFontWeight || '600'};
                --section-header-text-color: ${layoutConfig.globalStyles.sectionHeaderTextColor || '#1e293b'};
                --report-container-padding: ${layoutConfig.globalStyles.reportContainerPadding || '20px'};
                font-family: ${layoutConfig.globalStyles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'};
                font-size: ${layoutConfig.globalStyles.fontSize || '14px'};
                color: ${layoutConfig.globalStyles.textColor || '#1e293b'};
                background-color: ${layoutConfig.globalStyles.backgroundColor || '#ffffff'};
            }
            
            .section-container {
                border: 1px solid var(--border-color) !important;
                border-radius: var(--border-radius) !important;
                margin-bottom: var(--section-spacing) !important;
                background: ${layoutConfig.globalStyles.backgroundColor || '#ffffff'} !important;
            }
            
            .section-header {
                background: var(--section-header-background) !important;
                font-size: var(--section-header-font-size) !important;
                font-weight: var(--section-header-font-weight) !important;
                color: var(--section-header-text-color) !important;
                padding: var(--section-padding) !important;
            }
            
            .section-content {
                padding: var(--section-padding) !important;
            }
            
            .field-group {
                margin-bottom: var(--group-spacing) !important;
                padding: var(--group-padding) !important;
                border-radius: var(--border-radius) !important;
            }
            
            .ddr-content {
                padding: var(--report-container-padding) !important;
            }
        `;
    }
    
    // Add layout-specific custom CSS
    if (layoutConfig.globalStyles?.customCSS) {
        css += `\n/* Global Custom CSS */\n${layoutConfig.globalStyles.customCSS}\n`;
    }
    
    // Add section-specific CSS
    if (layoutConfig.sections) {
        layoutConfig.sections.forEach(section => {
            if (section.enabled) {
                // Add table-specific custom CSS if available
                if (section.customTableCSS) {
                    const tableClassName = `table-section-${section.id}`;
                    css += `\n/* Custom CSS for ${section.name} table */\n`;
                    css += section.customTableCSS
                        .replace(/\btable\b/g, `.${tableClassName} table`)
                        .replace(/\bth\b/g, `.${tableClassName} th`)
                        .replace(/\btr\b/g, `.${tableClassName} tr`)
                        .replace(/\btd\b/g, `.${tableClassName} td`)
                        .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                            // Add !important to critical properties that might be overridden
                            const importantRules = rules.replace(
                                /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                                '$1$2 !important'
                            );
                            return `${selector}{${importantRules}}`;
                        }) + '\n';
                }
                
                // Add group-specific CSS
                if (section.groupSettings) {
                    Object.entries(section.groupSettings).forEach(([groupId, settings]) => {
                        if (settings.customCSS) {
                            const groupClassName = `field-group-${section.id}-${groupId}`;
                            css += `\n/* Custom CSS for ${section.name} - Group ${groupId} */\n`;
                            css += settings.customCSS
                                .replace(/\.field-group/g, `.${groupClassName}`)
                                .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                                    const importantRules = rules.replace(
                                        /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                                        '$1$2 !important'
                                    );
                                    return `${selector}{${importantRules}}`;
                                }) + '\n';
                        }
                    });
                }
            }
        });
    }
    
    return css;
};

// Process header HTML with template variables
const processHeaderHTML = async (layoutConfig, wells, selectedWellId, reportDate, currentReport, isNewReport) => {
    if (!layoutConfig) return '';

    const selectedWell = wells.find(w => w.WellID === parseInt(selectedWellId));

    // Get header HTML from config, fallback to default
    let headerHTML = layoutConfig.headerHTML || `<div class="report-main-header">
        <h1 class="report-main-title">Daily Drilling Report (DDR)</h1>
        <p class="report-subtitle">{{wellName}} | {{reportDate}}</p>
    </div>`;

    // Replace template variables - handle both current report and new report
    const dateToUse = isNewReport ? reportDate : (currentReport?.ReportDate || reportDate);
    headerHTML = headerHTML
        .replace(/\{\{wellName\}\}/g, selectedWell?.WellName || '-')
        .replace(/\{\{reportDate\}\}/g, formatDate(dateToUse))
        .replace(/\{\{operator\}\}/g, selectedWell?.Operator || '-')
        .replace(/\{\{field\}\}/g, selectedWell?.Field || '-')
        .replace(/\{\{wellType\}\}/g, selectedWell?.WellType || '-');

    // Handle logo template variables
    try {
        // Replace default logo {{logo}}
        if (headerHTML.includes('{{logo}}')) {
            const defaultLogoResponse = await apiFetch('/api/logo/default');
            const defaultLogoUrl = defaultLogoResponse.defaultLogoUrl || '';
            if (defaultLogoUrl) {
                headerHTML = headerHTML.replace(/\{\{logo\}\}/g, `<img src="${defaultLogoUrl}" alt="Logo" class="header-logo" style="max-height: 60px; max-width: 200px;" />`);
            } else {
                headerHTML = headerHTML.replace(/\{\{logo\}\}/g, '');
            }
        }

        // Replace specific logo {{logo:filename}}
        const logoMatches = headerHTML.match(/\{\{logo:([^}]+)\}\}/g);
        if (logoMatches) {
            for (const match of logoMatches) {
                const filename = match.match(/\{\{logo:([^}]+)\}\}/)[1];
                const logoUrl = `/uploads/logos/${filename}`;
                headerHTML = headerHTML.replace(match, `<img src="${logoUrl}" alt="Logo" class="header-logo" style="max-height: 60px; max-width: 200px;" />`);
            }
        }
    } catch (error) {
        console.error('Failed to load logo:', error);
        // Remove logo placeholders if loading fails
        headerHTML = headerHTML.replace(/\{\{logo[^}]*\}\}/g, '');
    }

    return headerHTML;
};

// Optimized components with minimal re-renders
const MemoizedInput = React.memo(({ value, onChange, ...props }) => (
    <input
        value={value || ''}
        onChange={onChange}
        {...props}
    />
));

const MemoizedTextarea = React.memo(({ value, onChange, ...props }) => (
    <textarea
        value={value || ''}
        onChange={onChange}
        {...props}
    />
));

const MemoizedFieldValue = React.memo(({ value, field, globalSettings }) => {
    const formattedValue = formatFieldValue(value, field, globalSettings);
    return <div dangerouslySetInnerHTML={{ __html: formattedValue }} />;
});

// Toast component with proper lifecycle management
const Toast = React.memo(({ message, onClose }) => {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(onClose, 4000);
            return () => clearTimeout(timer);
        }
    }, [message, onClose]);

    if (!message) return null;

    return (
        <div className="toast-notification">
            {message}
        </div>
    );
});



// Section component with isolated state management
const Section = React.memo(({ 
    config, 
    data, 
    isEditing, 
    editingData,
    onEdit,
    onEditForm,
    onSave,
    onCancel,
    onFieldChange,
    onMultiFieldChange,
    onAddRow,
    onRemoveRow,
    onCopyFromLastDDR,
    onToast,
    globalSettings,
    role,
    isSaving = false,
    isCollapsed = false,
    onToggleCollapse,
    sectionRef,
    onContextMenu,
    selectedWellId,
    reportDate,
    currentReportId
}) => {

    // State for showing/hiding last DDR data popup
    const [showLastDDRPopup, setShowLastDDRPopup] = useState(false);
    const [lastDDRData, setLastDDRData] = useState(null);
    const [lastDDRInfo, setLastDDRInfo] = useState(null);
    const [loadingLastDDR, setLoadingLastDDR] = useState(false);
    const [currentRowIndex, setCurrentRowIndex] = useState(0); // For navigating through multiple rows

    // Auto-close popup when exiting edit mode
    useEffect(() => {
        if (!isEditing && showLastDDRPopup) {
            console.log('🚫 Auto-closing Last DDR popup because edit mode was exited');
            setShowLastDDRPopup(false);
            setLastDDRData(null);
            setLastDDRInfo(null);
            setCurrentRowIndex(0);
        }
    }, [isEditing, showLastDDRPopup]);

    // Reset row index when new data is loaded
    useEffect(() => {
        if (lastDDRData && lastDDRData.length > 0) {
            setCurrentRowIndex(0);
        }
    }, [lastDDRData]);

    // Row navigation functions for table sections
    const handlePreviousRow = useCallback(() => {
        setCurrentRowIndex(prev => Math.max(0, prev - 1));
    }, []);

    const handleNextRow = useCallback(() => {
        setCurrentRowIndex(prev => Math.min((lastDDRData?.length || 1) - 1, prev + 1));
    }, [lastDDRData]);

    // Copy all rows function for table sections
    const handleAddAllRows = useCallback(() => {
        if (!lastDDRData || lastDDRData.length === 0 || config.presentationType !== 'table') {
            return;
        }

        // Check if there are any editing rows
        const currentEditingData = editingData || {};
        const currentTableRows = currentEditingData.tableRows || [];

        if (currentTableRows.length === 0) {
            alert('Please add at least one row to the table before copying all data from Last DDR.');
            return;
        }

        const confirmed = window.confirm(
            `This will copy all ${lastDDRData.length} rows from the last DDR to your current table.\n\n` +
            `Current rows: ${currentTableRows.length}\n` +
            `New rows to add: ${lastDDRData.length}\n\n` +
            `Do you want to proceed?`
        );

        if (confirmed) {
            // Add all rows from last DDR
            lastDDRData.forEach((row, index) => {
                // Add a new row for each last DDR row
                onAddRow(config.id);
                
                // Copy all field values to the newly added row
                setTimeout(() => {
                    config.fields
                        .filter(field => field.enabled)
                        .forEach(field => {
                            const value = row[field.dbColumn];
                            if (value !== null && value !== undefined && value !== '') {
                                onCopyFromLastDDR(config.id, field, value, currentTableRows.length + index);
                            }
                        });
                }, 100 * (index + 1)); // Stagger the operations
            });

            // Show success message
            setTimeout(() => {
                onToast(`✅ Added all ${lastDDRData.length} rows from last DDR`);
                setTimeout(() => onToast(''), 3000);
            }, 100 * lastDDRData.length + 500);
        }
    }, [lastDDRData, config, editingData, onAddRow, onCopyFromLastDDR]);

    // Enhanced copy function that handles row targeting for tables
    const handleCopyFromLastDDRToTargetRow = useCallback((field, value, sourceRowIndex = null) => {
        if (config.presentationType === 'table') {
            // For table sections, copy to the last editing row
            const currentEditingData = editingData || {};
            const currentTableRows = currentEditingData.tableRows || [];
            
            if (currentTableRows.length === 0) {
                alert('Please add a row to the table first before copying data from Last DDR.');
                return;
            }

            // Copy to the last row (most recently added)
            const targetRowIndex = currentTableRows.length - 1;
            onCopyFromLastDDR(config.id, field, value, targetRowIndex);
        } else {
            // For form sections, copy normally
            onCopyFromLastDDR(config.id, field, value);
        }
    }, [config, editingData, onCopyFromLastDDR]);

    // Function to fetch and toggle last DDR popup
    const handleShowLastDDR = useCallback(async () => {
        console.log('🔥 handleShowLastDDR called:', {
            showLastDDRPopup,
            loadingLastDDR,
            tableName: config.tableName,
            selectedWellId,
            reportDate
        });
        
        if (showLastDDRPopup) {
            // Hide last DDR popup
            console.log('🔥 Hiding popup');
            setShowLastDDRPopup(false);
            setLastDDRData(null);
            setLastDDRInfo(null);
            return;
        }

        if (!config.tableName || !selectedWellId) {
            console.warn('Cannot fetch last DDR data: missing tableName or wellId');
            return;
        }

        setLoadingLastDDR(true);
        try {
            console.log(`🔍 Fetching last DDR data for table: ${config.tableName}, well: ${selectedWellId}, beforeDate: ${reportDate}`);
            const response = await getLatestDDRData(selectedWellId, config.tableName, reportDate);
            
            // Enhanced debugging
            console.log(`🔍 Backend response for ${config.tableName}:`, {
                fullResponse: response,
                responseType: typeof response,
                isArray: Array.isArray(response),
                responseKeys: response ? Object.keys(response) : [],
                hasResponse: !!response,
                hasData: !!(response && response.data),
                dataType: response?.data ? typeof response.data : 'undefined',
                dataLength: Array.isArray(response?.data) ? response.data.length : 'not array',
                latestReportDate: response?.latestReportDate,
                latestReportId: response?.latestReportId,
                message: response?.message,
                // Check if response IS the data array (not wrapped)
                responseAsArray: Array.isArray(response) ? response.length : 'not array',
                firstElement: Array.isArray(response) && response.length > 0 ? response[0] : 'no first element'
            });

            // Check if we have any data at all (even if empty array)
            if (response && response.latestReportDate && response.latestReportId) {
                // We found a report, check if it has data
                const hasActualData = response.data && Array.isArray(response.data) && response.data.length > 0;
                
                if (hasActualData) {
                    console.log(`✅ Found previous DDR data for ${config.tableName}:`, response.data);
                    setLastDDRData(response.data);
                    setLastDDRInfo({
                        latestReportDate: response.latestReportDate,
                        latestReportId: response.latestReportId,
                        message: response.message
                    });
                    console.log('🔥 Setting showLastDDRPopup to TRUE (with data)');
                    setShowLastDDRPopup(true);
                } else {
                    console.log(`ℹ️ Found previous report but no data in ${config.tableName} table`);
                    // Show empty data popup with report info
                    setLastDDRData([]);
                    setLastDDRInfo({
                        latestReportDate: response.latestReportDate,
                        latestReportId: response.latestReportId,
                        message: response.message || `Previous report found but no data in ${config.tableName} table`
                    });
                    console.log('🔥 Setting showLastDDRPopup to TRUE (empty data)');
                    setShowLastDDRPopup(true);
                }
            } else {
                console.log(`ℹ️ No previous DDR report found for well ${selectedWellId} before ${reportDate}`);
                setLastDDRData(null);
                setLastDDRInfo(null);
                setShowLastDDRPopup(false);
                alert(`No previous DDR report found for ${config.name}`);
            }
        } catch (error) {
            console.error(`❌ Error fetching last DDR data:`, error);
            setLastDDRData(null);
            setLastDDRInfo(null);
            setShowLastDDRPopup(false);
            alert(`Error fetching last DDR data: ${error.message}`);
        } finally {
            setLoadingLastDDR(false);
        }
    }, [config.tableName, config.name, selectedWellId, reportDate, showLastDDRPopup]);

    
    const hasData = useMemo(() => {
        if (config.presentationType === 'table') {
            return Array.isArray(data) && data.length > 0;
        }
        // For form sections, data comes as an array but we need to check if the first record has data
        if (Array.isArray(data) && data.length > 0) {
            const firstRecord = data[0];
            return firstRecord && Object.keys(firstRecord).length > 0;
        }
        return false;
    }, [data, config.presentationType]);

    const isEmpty = !hasData && !isEditing && !isSaving;

    // Right-click context menu handler - use global context menu
    const handleContextMenu = useCallback((e) => {
        // Only show context menu if user has edit permissions and there's data
        if ((role === 'admin' || role === 'read-write') && hasData && !isEditing) {
            onContextMenu(e, config.id);
        }
    }, [role, hasData, isEditing, onContextMenu, config.id]);

    // Double-click to enter inline edit mode
    const handleDoubleClick = useCallback((e) => {
        e.preventDefault();
        
        // Only allow double-click edit if user has edit permissions and there's data
        if ((role === 'admin' || role === 'read-write') && hasData && !isEditing) {
            console.log('🖱️ Double-click detected, entering inline edit mode for:', config.name);
            onEdit(config.id);
        }
    }, [role, hasData, isEditing, onEdit, config.id, config.name]);



    const handleFieldChange = useCallback((fieldColumn, value, rowIndex = null) => {
        onFieldChange(config.id, fieldColumn, value, rowIndex);
    }, [config.id, onFieldChange]);

    // Multi-field change handler for lookup fields with lookupReturnColumn
    const handleMultiFieldChange = useCallback((fieldMappings, rowIndex = null) => {
        if (onMultiFieldChange) {
            onMultiFieldChange(config.id, fieldMappings, rowIndex);
        }
    }, [config.id, onMultiFieldChange]);

    const renderFieldInput = useCallback((field, value, rowIndex = null, sectionConfig) => {
        const onChange = (e) => handleFieldChange(field.dbColumn, e.target.value, rowIndex);
        const fieldFormat = field.format?.toLowerCase() || 'text';
        let fieldValue = value || '';
        
        // Check if this is a calculated field
        const isCalculated = isCalculatedField(field);
        
        // Debug: Field calculation check - Enhanced for troubleshooting
        console.log('🎯 RENDER FIELD DEBUG:', {
            fieldName: field.name,
            dbColumn: field.dbColumn,
            fieldFormat: fieldFormat,
            originalFormat: field.format,
            fieldValue: fieldValue,
            valueType: typeof fieldValue,
            hasCalculation: !!field.calculation,
            calculation: field.calculation,
            isCalculated,
            sectionId: sectionConfig?.id,
            rowIndex
        });
        
        // If it's a calculated field, calculate its value
        if (isCalculated && sectionConfig) {
            const currentFormData = rowIndex !== null 
                ? editingData[sectionConfig.id]?.tableRows?.[rowIndex] || {}
                : editingData[sectionConfig.id] || {};
            
            // Debug: Calculating field value
            if (field.calculation) {
                console.log('🧮 Calculating field value:', {
                    fieldName: field.name,
                    calculation: field.calculation,
                    currentFormData,
                    rowIndex,
                    availableFields: sectionConfig.fields?.map(f => ({ 
                        name: f.name, 
                        dbColumn: f.dbColumn,
                        hasCalculation: !!f.calculation 
                    }))
                });
            }
            
            if (field.calculation) {
                const calculatedValue = evaluateCalculation(field.calculation, currentFormData);
                // Debug: Calculation result
                if (field.calculation) {
                    console.log('🧮 Calculation result:', {
                        fieldName: field.name,
                        calculation: field.calculation,
                        input: currentFormData,
                        result: calculatedValue
                    });
                }
                
                if (calculatedValue !== '' && calculatedValue !== null && calculatedValue !== undefined) {
                    fieldValue = calculatedValue;
                }
            }
        }
        
        // Field validation logic
        const validateField = (val, format) => {
            if (!val) return { isValid: true, message: '' };
            
            console.log('🔍 Validating field:', { value: val, format: format, type: typeof val });
            
            switch (format) {
                case 'number':
                case 'currency':
                    const num = parseFloat(val);
                    if (isNaN(num)) {
                        return { isValid: false, message: 'Must be a valid number' };
                    }
                    break;
                case 'date':
                    const date = new Date(val);
                    if (isNaN(date.getTime())) {
                        return { isValid: false, message: 'Must be a valid date' };
                    }
                    break;
                case 'time':
                    // More flexible time validation that accepts various formats
                    // Accept: 8:30, 08:30, 14:30, etc.
                    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
                    // Also accept single digit formats during typing like 8:3, 8:, etc.
                    const partialTimeRegex = /^([0-1]?[0-9]|2[0-3]):?[0-5]?[0-9]?$/;
                    
                    // For partial inputs (during typing), be more lenient
                    if (typeof val === 'string' && val.length <= 5) {
                        if (partialTimeRegex.test(val) || val === '' || /^\d{1,2}:?$/.test(val)) {
                            console.log('🔍 Valid partial time input:', val);
                            return { isValid: true, message: '' };
                        }
                    }
                    
                    break;
            }
            return { isValid: true, message: '' };
        };
        
        const validation = validateField(fieldValue, fieldFormat);
        const fieldClassName = `form-control enhanced-${fieldFormat}${!validation.isValid ? ' error' : ''}${isCalculated ? ' calculated-field' : ''}`;
        
        console.log('🎨 Rendering field input:', {
            fieldName: field.name,
            dbColumn: field.dbColumn,
            format: fieldFormat,
            originalFormat: field.format,
            options: field.options,
            lookupTable: field.lookupTable,
            lookupValueColumn: field.lookupValueColumn,
            lookupDisplayColumn: field.lookupDisplayColumn,
            value: fieldValue,
            rowIndex,
            validation,
            isLookupField: isFieldLookup(field),
            isCalculated,
            calculation: field.calculation
        });

        // If this is a calculated field, render it as read-only with calculation info
        if (isCalculated) {
            console.log('🟢 RENDERING CALCULATED FIELD:', {
                fieldName: field.name,
                calculation: field.calculation,
                value: fieldValue
            });
            
            return (
                <div className="field-wrapper calculated-field-wrapper" style={{ 
                    background: '#f0fdf4'
                }}>
                    <div className="calculated-field-container">
                        <input
                            type="text"
                            value={fieldValue}
                            readOnly
                            className={`${fieldClassName} calculated-readonly`}
                            placeholder="🧮 Auto-calculated"
                            title={`Calculated: ${field.calculation || 'Auto-calculated field'}`}
                            style={{
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px',
                                borderRadius: rowIndex !== null ? '4px' : '6px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                backgroundColor: '#dcfce7',
                                color: '#15803d',
                                cursor: 'not-allowed',
                                width: '90%'
                            }}
                        />
                        <div className="calculated-indicator" title={`Formula: ${field.calculation}`} style={{
                            color: '#22c55e',
                            fontSize: '16px',
                        }}>
                        </div>
                    </div>
                    {field.calculation && (
                        <div className="calculation-info" style={{ 
                            fontSize: '10px', 
                            color: '#15803d', 
                            marginTop: '4px',
                            fontStyle: 'italic',
                            background: '#bbf7d0',
                            padding: '2px 6px',
                            borderRadius: '4px'
                        }}>
                            Formula: {field.calculation}
                        </div>
                    )}
                </div>
            );
        }

        // Enhanced field rendering based on format
        switch (fieldFormat) {
            case 'textarea':
                return (
                    <div className="field-wrapper">
                        <MemoizedTextarea
                            value={fieldValue}
                            onChange={onChange}
                            className={fieldClassName.replace('enhanced-text', 'enhanced-textarea')}
                            rows={field.rows || 3}
                            placeholder={field.placeholder || field.name}
                            title={!validation.isValid ? validation.message : ''}
                            style={{
                                minHeight: rowIndex !== null ? '50px' : '60px', // Smaller in table cells
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                width: '100%',
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px' // Smaller padding in tables
                            }}
                        />
                        {!validation.isValid && (
                            <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                {validation.message}
                            </div>
                        )}
                    </div>
                );

            case 'date':
                return (
                    <div className="field-wrapper">
                        <input
                            type="date"
                            value={fieldValue ? fieldValue.split('T')[0] : ''}
                            onChange={onChange}
                            className={fieldClassName.replace('enhanced-text', 'enhanced-date')}
                            title={!validation.isValid ? validation.message : ''}
                            style={{
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px', // Smaller padding in tables
                                border: '1px solid #d1d5db',
                                borderRadius: rowIndex !== null ? '4px' : '6px', // Smaller radius in tables
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                width: '100%'
                            }}
                        />
                        {!validation.isValid && (
                            <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                {validation.message}
                            </div>
                        )}
                    </div>
                );

            case 'time':
                // Format time value for HTML time input (HH:MM format)
                const formatTimeForInput = (value) => {
                    if (!value) return '';
                    
                    console.log('🕐 formatTimeForInput - Input value:', value, typeof value);
                    
                    // Don't format partial input (like "1", "14", "14:")
                    if (typeof value === 'string' && value.length <= 2) {
                        console.log('🕐 Very short input detected, returning as-is:', value);
                        return value;
                    }
                    
                    // Handle different time formats
                    if (typeof value === 'string') {
                        // If it's already in HH:MM format, return as is
                        if (/^\d{1,2}:\d{2}$/.test(value)) {
                            console.log('🕐 Already in HH:MM format:', value);
                            return value;
                        }
                        
                        // If it's a full datetime string, extract time part WITHOUT timezone conversion
                        if (value.includes('T')) {
                            const timePart = value.split('T')[1];
                            if (timePart) {
                                const timeOnly = timePart.substring(0, 5); // Get HH:MM
                                console.log('🕐 Extracted from datetime:', timeOnly);
                                return timeOnly;
                            }
                        }
                        
                        // If it's in format like "14:30:00", extract HH:MM
                        if (/^\d{1,2}:\d{2}:\d{2}/.test(value)) {
                            const timeOnly = value.substring(0, 5);
                            console.log('🕐 Extracted from HH:MM:SS:', timeOnly);
                            return timeOnly;
                        }
                        
                        // If it's just time like "14:30" or "9:30", format properly (but not partial input)
                        if (/^\d{1,2}:\d{2}$/.test(value)) {
                            // Only pad if it's a complete time (has both hours and minutes)
                            const parts = value.split(':');
                            if (parts[1] && parts[1].length === 2) {
                                const formatted = `${parts[0].padStart(2, '0')}:${parts[1]}`;
                                console.log('🕐 Padded single digit hours:', formatted);
                                return formatted;
                            }
                        }
                        
                        // Handle time without colon like "1430" -> "14:30"
                        if (/^\d{3,4}$/.test(value)) {
                            if (value.length === 3) {
                                // "930" -> "09:30"
                                const formatted = `0${value.charAt(0)}:${value.substring(1)}`;
                                console.log('🕐 Formatted 3-digit time:', formatted);
                                return formatted;
                            } else if (value.length === 4) {
                                // "1430" -> "14:30"
                                const formatted = `${value.substring(0, 2)}:${value.substring(2)}`;
                                console.log('🕐 Formatted 4-digit time:', formatted);
                                return formatted;
                            }
                        }
                        
                        // For partial inputs during typing, return as-is
                        if (/^\d{1,2}:?\d{0,2}$/.test(value)) {
                            console.log('🕐 Partial time input, returning as-is:', value);
                            return value;
                        }
                    }
                    
                    // If it's a Date object or timestamp, extract time without timezone conversion
                    if (value instanceof Date || !isNaN(Date.parse(value))) {
                        try {
                            const date = new Date(value);
                            // Use getHours and getMinutes to avoid timezone issues
                            const hours = date.getHours().toString().padStart(2, '0');
                            const minutes = date.getMinutes().toString().padStart(2, '0');
                            const formatted = `${hours}:${minutes}`;
                            console.log('🕐 Formatted from Date object:', formatted);
                            return formatted;
                        } catch (e) {
                            console.log('🕐 Error parsing date:', e);
                            return value;
                        }
                    }
                    
                    console.log('🕐 Returning original value:', value);
                    return value;
                };

                const formattedTimeValue = formatTimeForInput(fieldValue);
                console.log('🕐 Final formatted time value:', formattedTimeValue);

                // Handle time change to ensure consistent format
                const handleTimeChange = (e) => {
                    const timeValue = e.target.value; // This will be in HH:MM format from HTML time input
                    console.log('🕐 Time changed to:', timeValue);
                    onChange({ target: { value: timeValue } });
                };

                // Check if field has specific time format configuration
                const fieldTimeFormat = field.timeFormat || 'HH:mm'; // Default to 24-hour if not specified
                const use24Hour = fieldTimeFormat.includes('HH'); // Check if 24-hour format is specified
                
                console.log('🕐 Time field configuration:', {
                    fieldName: field.name,
                    dbColumn: field.dbColumn,
                    timeFormat: fieldTimeFormat,
                    use24Hour: use24Hour,
                    fieldValue: fieldValue,
                    fieldValueType: typeof fieldValue
                });

                // Custom time input handler - NO formatting during typing
                const handleCustomTimeChange = (e) => {
                    let inputValue = e.target.value;
                    console.log('🕐 Raw input during typing:', inputValue);
                    
                    // ONLY filter out invalid characters - NO formatting at all
                    const cleanValue = inputValue.replace(/[^\d:]/g, '');
                    
                    // Basic validation: max 1 colon, max 5 characters
                    const colonCount = (cleanValue.match(/:/g) || []).length;
                    
                    if (colonCount <= 1 && cleanValue.length <= 5) {
                        console.log('🕐 Passing through unchanged during typing:', cleanValue);
                        // Pass the raw value directly to the field - no processing
                        onChange({ target: { value: cleanValue } });
                    } else {
                        console.log('🕐 Rejecting invalid input:', cleanValue);
                        // Don't change anything if invalid
                    }
                };

                // For 24-hour format, use a text input with pattern validation instead of type="time"
                if (use24Hour) {
                    // Extract time from field value for 24-hour input
                    const extractTimeFromValue = (value) => {
                        if (!value) return '';
                        
                        console.log('🕐 Extracting time from value:', value, typeof value);
                        
                        // If it's already a simple time format like "14:30", return as-is
                        if (typeof value === 'string' && /^\d{1,2}:\d{0,2}$/.test(value)) {
                            console.log('🕐 Already simple time format:', value);
                            return value;
                        }
                        
                        // If it's a datetime string like "1970-01-01T14:30:00.000Z"
                        if (typeof value === 'string' && value.includes('T')) {
                            const timePart = value.split('T')[1];
                            if (timePart) {
                                const timeOnly = timePart.substring(0, 5); // Get HH:MM
                                console.log('🕐 Extracted time from datetime:', timeOnly);
                                return timeOnly;
                            }
                        }
                        
                        // If it's a Date object
                        if (value instanceof Date) {
                            const hours = value.getHours().toString().padStart(2, '0');
                            const minutes = value.getMinutes().toString().padStart(2, '0');
                            const timeOnly = `${hours}:${minutes}`;
                            console.log('🕐 Extracted time from Date object:', timeOnly);
                            return timeOnly;
                        }
                        
                        // Try to parse as date if it looks like a date string
                        if (typeof value === 'string' && !isNaN(Date.parse(value))) {
                            try {
                                const date = new Date(value);
                                const hours = date.getHours().toString().padStart(2, '0');
                                const minutes = date.getMinutes().toString().padStart(2, '0');
                                const timeOnly = `${hours}:${minutes}`;
                                console.log('🕐 Extracted time from parsed date:', timeOnly);
                                return timeOnly;
                            } catch (e) {
                                console.log('�� Error parsing date:', e);
                            }
                        }
                        
                        console.log('🕐 Returning original value:', value);
                        return value;
                    };
                    
                    // Only extract time from datetime if the current value looks like a datetime string
                    // Otherwise, use the field value as-is (for typing scenarios)
                    let displayValue = fieldValue || '';
                    
                    // Only do extraction if it looks like a datetime string (not simple time format)
                    if (typeof fieldValue === 'string' && 
                        (fieldValue.includes('T') || fieldValue.length > 8) && 
                        !(/^\d{1,2}:\d{0,2}$/.test(fieldValue))) {
                        displayValue = extractTimeFromValue(fieldValue);
                        console.log('🕐 Extracted time from datetime for display:', displayValue);
                    } else {
                        console.log('🕐 Using field value as-is for display:', displayValue);
                    }
                    
                    return (
                        <div className="field-wrapper">
                            <input
                                type="text"
                                value={displayValue}
                                onChange={handleCustomTimeChange}
                                className={fieldClassName.replace('enhanced-text', 'enhanced-time-24h')}
                                placeholder="HH:MM (e.g., 14:30)"
                                pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                                title={!validation.isValid ? validation.message : 'Enter time in 24-hour format (HH:MM). You can type hours and minutes separately.'}
                                maxLength="5"
                                style={{
                                    padding: rowIndex !== null ? '4px 6px' : '6px 8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: rowIndex !== null ? '3px' : '4px',
                                    fontSize: rowIndex !== null ? '13px' : '14px',
                                    fontFamily: 'monospace', // Use monospace for better time display
                                    width: '100%',
                                    minWidth: rowIndex !== null ? '80px' : '100px',
                                    textAlign: 'center',
                                    letterSpacing: '1px'
                                }}
                                onBlur={(e) => {
                                    // Format on blur to ensure consistency
                                    const value = e.target.value.trim();
                                    console.log('🕐 Custom time input blur:', value);
                                    
                                    if (value && value !== '') {
                                        let hours = null;
                                        let minutes = null;
                                        
                                        // Handle different input formats
                                        if (value.includes(':')) {
                                            // Format: "14:30", "14:", "14:3", ":30", etc.
                                            const parts = value.split(':');
                                            hours = parts[0] ? parseInt(parts[0]) : 0;
                                            minutes = parts[1] ? parseInt(parts[1]) : 0;
                                        } else {
                                            // Format: "14", "1430", "930", etc.
                                            if (value.length <= 2) {
                                                // Single or double digit hours: "1", "14"
                                                hours = parseInt(value);
                                                minutes = 0;
                                            } else if (value.length === 3) {
                                                // Three digits: "930" -> "9:30"
                                                hours = parseInt(value.charAt(0));
                                                minutes = parseInt(value.substring(1));
                                            } else if (value.length === 4) {
                                                // Four digits: "1430" -> "14:30"
                                                hours = parseInt(value.substring(0, 2));
                                                minutes = parseInt(value.substring(2));
                                            } else {
                                                console.log('🕐 Invalid format, too many digits');
                                                hours = null;
                                                minutes = null;
                                            }
                                        }
                                        
                                        // Validate and format if we have valid values
                                        if (hours !== null && minutes !== null && !isNaN(hours) && !isNaN(minutes)) {
                                            // Validate ranges
                                            if (hours < 0) hours = 0;
                                            if (hours > 23) hours = 23;
                                            if (minutes < 0) minutes = 0;
                                            if (minutes > 59) minutes = 59;
                                            
                                            const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                                            console.log('🕐 Formatting time from', value, 'to', formatted);
                                            
                                            if (formatted !== value) {
                                                e.target.value = formatted;
                                                onChange({ target: { value: formatted } });
                                            }
                                        } else {
                                            console.log('🕐 Invalid time format, clearing field');
                                            e.target.value = '';
                                            onChange({ target: { value: '' } });
                                        }
                                    }
                                }}
                                onKeyDown={(e) => {
                                    // Allow only numbers, colon, backspace, delete, arrow keys, tab
                                    const allowedKeys = [
                                        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                                        'Tab', 'Home', 'End', 'Enter'
                                    ];
                                    
                                    // Allow numbers (0-9) and colon
                                    const isNumber = /^[0-9]$/.test(e.key);
                                    const isColon = e.key === ':';
                                    const isAllowedKey = allowedKeys.includes(e.key);
                                    
                                    console.log('🕐 Key pressed:', e.key, 'isNumber:', isNumber, 'isColon:', isColon, 'isAllowed:', isAllowedKey);
                                    
                                    if (!isNumber && !isColon && !isAllowedKey) {
                                        console.log('🕐 Preventing key:', e.key);
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <div className="time-format-hint" style={{ 
                                fontSize: '11px', 
                                color: '#6b7280', 
                                marginTop: '2px',
                                fontStyle: 'italic'
                            }}>
                                24-hour format (HH:MM)
                            </div>
                            {!validation.isValid && (
                                <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                    {validation.message}
                                </div>
                            )}
                        </div>
                    );
                } else {
                    // For 12-hour format, use the standard time input
                    return (
                        <div className="field-wrapper">
                            <input
                                type="time"
                                value={formattedTimeValue}
                                onChange={handleTimeChange}
                                className={fieldClassName.replace('enhanced-text', 'enhanced-time')}
                                title={!validation.isValid ? validation.message : ''}
                                step="60"
                                style={{
                                    padding: rowIndex !== null ? '4px 6px' : '6px 8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: rowIndex !== null ? '3px' : '4px',
                                    fontSize: rowIndex !== null ? '13px' : '14px',
                                    fontFamily: 'inherit',
                                    width: '100%',
                                    minWidth: rowIndex !== null ? '80px' : '100px'
                                }}
                            />
                            <div className="time-format-hint" style={{ 
                                fontSize: '11px', 
                                color: '#6b7280', 
                                marginTop: '2px',
                                fontStyle: 'italic'
                            }}>
                                12-hour format
                            </div>
                            {!validation.isValid && (
                                <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                    {validation.message}
                                </div>
                            )}
                        </div>
                    );
                }

            case 'number':
            case 'currency':
                return (
                    <div className="field-wrapper">
                        <input
                            type="number"
                            value={fieldValue}
                            onChange={onChange}
                            className={fieldClassName.replace('enhanced-text', 'enhanced-number')}
                            placeholder={field.placeholder || field.name}
                            step={field.numberFormat?.includes('.') ? 'any' : '1'}
                            title={!validation.isValid ? validation.message : ''}
                            style={{
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px', // Smaller padding in tables
                                border: '1px solid #d1d5db',
                                borderRadius: rowIndex !== null ? '4px' : '6px', // Smaller radius in tables
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                textAlign: 'right',
                                width: '100%'
                            }}
                        />
                        {!validation.isValid && (
                            <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                {validation.message}
                            </div>
                        )}
                    </div>
                );

            case 'select':
            case 'dropdown':
                // Extract options from field configuration or predefined lists
                const getFieldOptions = () => {
                    // First check if field has explicit options
                    if (field.options && Array.isArray(field.options)) {
                        return field.options;
                    }
                    
                    // Check if field has selectOptions property
                    if (field.selectOptions && Array.isArray(field.selectOptions)) {
                        return field.selectOptions;
                    }
                    
                    // Predefined options for common fields based on database column names
                    const commonOptions = {
                        'WellStatus': ['Active', 'Inactive', 'Suspended', 'Abandoned', 'Planning'],
                        'WellType': ['Exploration', 'Development', 'Appraisal', 'Injection', 'Water Injection', 'Gas Injection'],
                        'Phase': ['Planning', 'Drilling', 'Completion', 'Production', 'Workover', 'Abandonment'],
                        'Status': ['Active', 'Inactive', 'Pending', 'Complete', 'On Hold', 'Cancelled'],
                        'Priority': ['High', 'Medium', 'Low', 'Critical', 'Normal'],
                        'Weather': ['Clear', 'Cloudy', 'Rainy', 'Stormy', 'Foggy', 'Windy', 'Snow'],
                        'Operator': ['Company A', 'Company B', 'Company C', 'Joint Venture'],
                        'Field': ['Field A', 'Field B', 'Field C', 'New Field'],
                        'Location': ['Onshore', 'Offshore', 'Shallow Water', 'Deep Water'],
                        'RigType': ['Land Rig', 'Jack-up', 'Semi-submersible', 'Drillship'],
                        'ActivityType': ['Drilling', 'Tripping', 'Circulation', 'Logging', 'Casing', 'Cementing', 'Testing'],
                        'MudType': ['Water Base', 'Oil Base', 'Synthetic', 'Foam', 'Air'],
                        'FormationType': ['Sandstone', 'Limestone', 'Shale', 'Granite', 'Salt']
                    };
                    
                    return commonOptions[field.dbColumn] || [];
                };

                const options = getFieldOptions();
                
                return (
                    <div className="field-wrapper">
                        <select
                            value={fieldValue}
                            onChange={onChange}
                            className={fieldClassName.replace('enhanced-text', 'enhanced-select')}
                            style={{
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px', // Smaller padding in tables
                                border: '1px solid #d1d5db',
                                borderRadius: rowIndex !== null ? '4px' : '6px', // Smaller radius in tables
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            <option value="">Select {field.name}...</option>
                            {options.map((option, idx) => (
                                <option key={idx} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        {options.length === 0 && (
                            <div className="field-info-message" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                No predefined options available
                            </div>
                        )}
                    </div>
                );

            case 'boolean':
            case 'checkbox':
                return (
                    <div className="field-wrapper">
                        <div className="enhanced-checkbox-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                checked={fieldValue === true || fieldValue === 'true' || fieldValue === '1'}
                                onChange={(e) => handleFieldChange(field.dbColumn, e.target.checked, rowIndex)}
                                className="enhanced-checkbox"
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <label style={{ fontSize: '14px', cursor: 'pointer', userSelect: 'none' }}>
                                {field.name}
                            </label>
                        </div>
                    </div>
                );

            case 'lookup':
                // Handle lookup fields specifically
                console.log('🔍 DDR_v2: Rendering lookup field:', {
                    fieldName: field.name,
                    dbColumn: field.dbColumn,
                    format: field.format,
                    options: field.options,
                    lookupTable: field.lookupTable,
                    lookupValueColumn: field.lookupValueColumn,
                    lookupDisplayColumn: field.lookupDisplayColumn,
                    lookupReturnColumn: field.lookupReturnColumn,
                    value: fieldValue
                });
                
                return (
                    <div className="field-wrapper">
                        <LookupField
                            field={field}
                            value={fieldValue}
                            onChange={(newValue) => handleFieldChange(field.dbColumn, newValue, rowIndex)}
                            onMultiFieldChange={(fieldMappings) => handleMultiFieldChange(fieldMappings, rowIndex)}
                            placeholder={field.placeholder || `Select ${field.name}...`}
                            disabled={false}
                            className="form-control enhanced-lookup"
                        />
                    </div>
                );

            case 'text':
            default:
                // Check if field is configured as lookup (fallback check for column config)
                if (field.options === 'lookup' && field.lookupTable && field.lookupValueColumn && field.lookupDisplayColumn) {
                    console.log('🔍 DDR_v2: Rendering lookup field (fallback):', {
                        fieldName: field.name,
                        dbColumn: field.dbColumn,
                        format: field.format,
                        options: field.options,
                        lookupTable: field.lookupTable,
                        lookupValueColumn: field.lookupValueColumn,
                        lookupDisplayColumn: field.lookupDisplayColumn,
                        lookupReturnColumn: field.lookupReturnColumn,
                        value: fieldValue
                    });
                    
                    return (
                        <div className="field-wrapper">
                            <LookupField
                                field={field}
                                value={fieldValue}
                                onChange={(newValue) => handleFieldChange(field.dbColumn, newValue, rowIndex)}
                                onMultiFieldChange={(fieldMappings) => handleMultiFieldChange(fieldMappings, rowIndex)}
                                placeholder={field.placeholder || `Select ${field.name}...`}
                                disabled={false}
                                className="form-control enhanced-lookup"
                            />
                        </div>
                    );
                }
                // Standard text input
                return (
                    <div className="field-wrapper">
                        <MemoizedInput
                            type="text"
                            value={fieldValue}
                            onChange={onChange}
                            className={fieldClassName}
                            placeholder={field.placeholder || field.name}
                            title={!validation.isValid ? validation.message : ''}
                            style={{
                                padding: rowIndex !== null ? '6px 8px' : '8px 12px', // Smaller padding in tables
                                border: '1px solid #d1d5db',
                                borderRadius: rowIndex !== null ? '4px' : '6px', // Smaller radius in tables
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                width: '100%'
                            }}
                        />
                        {!validation.isValid && (
                            <div className="field-error-message" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                {validation.message}
                            </div>
                        )}
                    </div>
                );
        }
    }, [handleFieldChange, editingData]);

    // Note: Removed inline last DDR display - now using popup instead

    const renderTableSection = () => {
        const tableData = isEditing ? editingData?.tableRows || [] : (Array.isArray(data) ? data : []);
        const tableClassName = `table-section-${config.id}`;
        
        // Debug: Show table section fields
        console.log('📊 TABLE SECTION DEBUG:', {
            sectionId: config.id,
            sectionName: config.name,
            isEditing,
            tableRowsCount: tableData.length,
            calculatedFields: config.fields?.filter(f => f.calculation).map(f => ({
                name: f.name,
                dbColumn: f.dbColumn,
                calculation: f.calculation
            }))
        });
        
        return (
            <div className={`table-container ${tableClassName}`}>
                {/* Add custom table CSS if provided */}
                {config.customTableCSS && (
                    <style>
                        {config.customTableCSS
                            .replace(/\btable\b/g, `.${tableClassName} table`)
                            .replace(/\bth\b/g, `.${tableClassName} th`)
                            .replace(/\btr\b/g, `.${tableClassName} tr`)
                            .replace(/\btd\b/g, `.${tableClassName} td`)
                            .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                                // Add !important to critical properties that might be overridden
                                const importantRules = rules.replace(
                                    /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                                    '$1$2 !important'
                                );
                                return `${selector}{${importantRules}}`;
                            })
                        }
                    </style>
                )}

                <table className="section-table">
                    <thead>
                        <tr>
                            {config.fields
                                .filter(field => field.enabled)
                                .map(field => (
                                    <th key={field.id} style={{ width: field.width !== 'auto' ? field.width : undefined }}>
                                        {field.name}
                                    </th>
                                ))
                            }
                            {isEditing && <th style={{ width: '60px' }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.length > 0 ? (
                            tableData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {config.fields
                                        .filter(field => field.enabled)
                                        .map(field => (
                                            <td key={field.id}>
                                                {isEditing ? (
                                                    renderFieldInput(field, row[field.dbColumn] || '', rowIndex, config)
                                                ) : (
                                                    <MemoizedFieldValue
                                                        value={row[field.dbColumn]}
                                                        field={field}
                                                        globalSettings={globalSettings}
                                                    />
                                                )}
                                            </td>
                                        ))
                                    }
                                    {isEditing && (
                                        <td>
                                            <button 
                                                className="remove-table-row-btn"
                                                onClick={() => onRemoveRow(config.id, rowIndex)}
                                                title="Remove row"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={config.fields.filter(f => f.enabled).length + (isEditing ? 1 : 0)}>
                                    {isEditing ? 'Click "Add Row" to add data' : 'No data available'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {isEditing && (
                    <div className="table-editing-controls-bottom">
                        <button 
                            className="add-table-row-btn"
                            onClick={() => onAddRow(config.id)}
                        >
                            ➕ Add Row
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderFormSection = () => {
        // For form sections, data comes as an array, so we need to get the first record
        const dataRecord = Array.isArray(data) && data.length > 0 ? data[0] : {};
        const formData = isEditing ? editingData || {} : dataRecord;
        const enabledFields = config.fields.filter(field => field.enabled);
        
        // Debug: Show all fields in this section
        console.log('📋 SECTION FIELDS DEBUG:', {
            sectionId: config.id,
            sectionName: config.name,
            totalFields: config.fields?.length,
            enabledFields: enabledFields.length,
            calculatedFields: enabledFields.filter(f => f.calculation).map(f => ({
                name: f.name,
                dbColumn: f.dbColumn,
                calculation: f.calculation
            })),
            allFields: enabledFields.map(f => ({
                name: f.name,
                dbColumn: f.dbColumn,
                hasCalculation: !!f.calculation
            }))
        });

        // Group fields by their group property (like old implementation)
        const fieldGroups = enabledFields.reduce((groups, field) => {
            const groupId = field.group || 1;
            if (!groups[groupId]) {
                groups[groupId] = [];
            }
            groups[groupId].push(field);
            return groups;
        }, {});

        // Get group settings with fallback
        const groupSettings = config.groupSettings || {
            1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
        };

        return (
            <div className="form-container">
                {Object.entries(fieldGroups)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([groupId, fields]) => {
                        const settings = groupSettings[groupId] || { itemsPerRow: 2, customCSS: '', name: `Group ${groupId}`, showGroupName: false };
                        const groupClassName = `field-group-${config.id}-${groupId}`;

                        return (
                            <div key={groupId}
                                className={`field-group ${groupClassName}`}
                                style={{
                                    gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                    ...(settings.customCSS && {
                                        background: 'unset',
                                        padding: 'unset',
                                        border: 'unset',
                                        borderRadius: 'unset',
                                        margin: 'unset'
                                    })
                                }}>
                                
                                {/* Add scoped custom CSS if provided */}
                                {settings.customCSS && (
                                    <style>
                                        {settings.customCSS
                                            .replace(/\.field-group/g, `.${groupClassName}`)
                                            .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                                                // Add !important to critical properties that might be overridden
                                                const importantRules = rules.replace(
                                                    /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                                                    '$1$2 !important'
                                                );
                                                return `${selector}{${importantRules}}`;
                                            })
                                        }
                                    </style>
                                )}

                                {/* Show group title based on showGroupName setting */}
                                {settings.showGroupName && (
                                    <div className="field-group-title">{settings.name}</div>
                                )}

                                {/* Check if this is an Excel-style template that needs table structure */}
                                {settings.customCSS && settings.customCSS.includes('.field-grid') ? (
                                    // Excel table structure with support for multiple columns
                                    <div className="field-grid"
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                            gap: '0'
                                        }}>
                                        {fields.map(field => (
                                            field.width === 'full' ? (
                                                // Full width field spans all columns
                                                <div key={field.id}
                                                    className="field-row"
                                                    style={{
                                                        gridColumn: '1 / -1',
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 2fr'
                                                    }}>
                                                    <div className="label">{field.name}</div>
                                                    <div className="value">
                                                        {isEditing ? (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {renderFieldInput(field, formData[field.dbColumn], null, config)}
                                                                {field.calculationFunction && (
                                                                    <button
                                                                        type="button"
                                                                        className="cal-btn"
                                                                        onClick={() => handleCustomCalculate(field)}
                                                                        style={{ marginLeft: '8px' }}
                                                                        title="Calculate"
                                                                    >
                                                                        Cal
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <MemoizedFieldValue
                                                                value={formData[field.dbColumn]}
                                                                field={field}
                                                                globalSettings={globalSettings}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                // Regular field - each field is a grid item that contains label+value
                                                <div key={field.id} className="field-row">
                                                    <div className="label">{field.name}</div>
                                                    <div className="value">
                                                        {isEditing ? (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {renderFieldInput(field, formData[field.dbColumn], null, config)}
                                                                {field.calculationFunction && (
                                                                    <button
                                                                        type="button"
                                                                        className="cal-btn"
                                                                        onClick={() => handleCustomCalculate(field)}
                                                                        style={{ marginLeft: '8px' }}
                                                                        title="Calculate"
                                                                    >
                                                                        Cal
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <MemoizedFieldValue
                                                                value={formData[field.dbColumn]}
                                                                field={field}
                                                                globalSettings={globalSettings}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                ) : (
                                    // Standard grid structure
                                    <div className="field-group-content"
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                            gap: '16px'
                                        }}>
                                        {fields.map(field => (
                                            <div key={field.id}
                                                className={`label-value-item ${field.width === 'full' ? 'field-width-full' : ''}`}
                                                style={field.width === 'full' ? { gridColumn: '1 / -1' } : {}}>
                                                <span className="label">{field.name}</span>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        {renderFieldInput(field, formData[field.dbColumn], null, config)}
                                                        {field.calculationFunction && (
                                                            <button
                                                                type="button"
                                                                className="cal-btn"
                                                                onClick={() => handleCustomCalculate(field)}
                                                                style={{ marginLeft: '8px' }}
                                                                title="Calculate"
                                                            >
                                                                Cal
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="value">
                                                        <MemoizedFieldValue
                                                            value={formData[field.dbColumn]}
                                                            field={field}
                                                            globalSettings={globalSettings}
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                }
            </div>
        );
    };

    // Custom calculation handler
    const handleCustomCalculate = useCallback(async (field) => {
        const func = customCalculations[field.calculationFunction];
        if (!func) return;
        try {
            const sectionEditingData = editingData || {};
            const result = await func({ selectedWellId, reportDate, currentReportId, sectionEditingData });
            onFieldChange(config.id, field.dbColumn, result);
            onToast(`Calculated ${field.name}: ${result}`);
            setTimeout(() => onToast(''), 3000);
        } catch (error) {
            console.error('Calculation error:', error);
            alert(`Calculation failed for ${field.name}`);
        }
    }, [selectedWellId, reportDate, currentReportId, config.id, editingData, onFieldChange, onToast]);

    return (
        <div 
            ref={sectionRef}
            className="section-container" 
            onContextMenu={handleContextMenu} 
            onDoubleClick={handleDoubleClick}
            data-has-data={hasData}
            data-can-edit={role === 'admin' || role === 'read-write'}
            data-is-editing={isEditing}
            data-section-id={config.id}
        >
            <div className="section-header">
                <div className="section-title" onClick={() => onToggleCollapse && onToggleCollapse(config.id)}>
                    <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                        {isCollapsed ? '▶' : '▼'}
                    </span>
                    {config.name}
                </div>
                
                <div className="section-actions">
                    {isEditing ? (
                        <div className="editing-buttons">
                            {config.tableName && config.keyColumn === 'reportId' && (
                                <button 
                                    className="show-last-ddr-btn"
                                    onClick={handleShowLastDDR}
                                    disabled={loadingLastDDR}
                                    title={showLastDDRPopup ? "Hide last DDR popup" : "Show last DDR data"}
                                >
                                    {loadingLastDDR ? '⏳' : showLastDDRPopup ? 'Hide Last DDR' : '👁️ Show Last DDR'}
                                </button>
                            )}
                            <button 
                                className="save-btn"
                                onClick={() => onSave(config.id)}
                                disabled={isSaving}
                            >
                                {isSaving ? '⏳ Saving...' : '💾 Save'}
                            </button>
                            <button 
                                className="cancel-btn"
                                onClick={() => onCancel(config.id)}
                                disabled={isSaving}
                            >
                                ❌ Cancel
                            </button>
                            <button
                                className="full-form-edit-btn"
                                onClick={() => onEditForm(config.id)}
                                disabled={isSaving}
                                title="Full Form Edit"
                            >
                                📝 Full Form Edit
                            </button>
                        </div>
                    ) : (
                        hasData && (role === 'admin' || role === 'read-write') && (
                            <div className="section-info">
                                <span className="edit-hints">
                                    <span className="hint-item">Double-click to edit</span>
                                    <span className="hint-separator">•</span>
                                    <span className="hint-item">Right-click for options</span>
                                </span>
                            </div>
                        )
                    )}
                </div>
            </div>

            {!isCollapsed && (
                <div className="section-content">
                    {isEmpty && (role === 'admin' || role === 'read-write') ? (
                        <div className="empty-section">
                            <p>No data available</p>
                            <button 
                                className="add-data-btn"
                                onClick={() => onEdit(config.id)}
                            >
                                ➕ Add Data
                            </button>
                        </div>
                    ) : (
                        config.presentationType === 'table' ? renderTableSection() : renderFormSection()
                    )}
                </div>
            )}

            {/* Last DDR Data Popup */}
            {showLastDDRPopup && (
                <div className="last-ddr-popup">
                    <div className="last-ddr-popup-header">
                        <h4>Last DDR Data - {config.name}</h4>
                        <div className="last-ddr-info">
                            {lastDDRInfo && (
                                <span>
                                    Report Date: {new Date(lastDDRInfo.latestReportDate).toLocaleDateString()} 
                                </span>
                            )}
                        </div>
                        <button 
                            className="close-last-ddr-popup" 
                            onClick={() => setShowLastDDRPopup(false)}
                            title="Close popup"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="last-ddr-popup-content">
                        {lastDDRData && lastDDRData.length > 0 ? (
                            config.presentationType === 'table' ? (
                                // Table view - render exactly like main DDR section but with copy buttons
                                <div className="last-ddr-section-container">
                                    {/* Navigation and Add All controls for multiple rows */}
                                    {lastDDRData.length > 1 && (
                                        <div className="last-ddr-controls">
                                            <div className="row-navigation">
                                                <span className="row-info">
                                                    Row {currentRowIndex + 1} of {lastDDRData.length}
                                                </span>
                                                <div className="nav-buttons">
                                                    <button
                                                        className="nav-btn"
                                                        onClick={handlePreviousRow}
                                                        disabled={currentRowIndex === 0}
                                                        title="Previous row"
                                                    >
                                                        ⬆️
                                                    </button>
                                                    <button
                                                        className="nav-btn"
                                                        onClick={handleNextRow}
                                                        disabled={currentRowIndex === lastDDRData.length - 1}
                                                        title="Next row"
                                                    >
                                                        ⬇️
                                                    </button>
                                                </div>
                                            </div>
                                            <button
                                                className="add-all-btn"
                                                onClick={handleAddAllRows}
                                                title="Copy all rows from last DDR"
                                            >
                                                📋 Add All ({lastDDRData.length} rows)
                                            </button>
                                        </div>
                                    )}

                                    <div className="table-container">
                                        <table className="section-table last-ddr-section-table">
                                            <thead>
                                                <tr>
                                                    {config.fields
                                                        .filter(field => field.enabled)
                                                        .map(field => {
                                                            const value = lastDDRData[currentRowIndex][field.dbColumn];
                                                            const hasValue = value !== null && value !== undefined && value !== '';
                                                            
                                                            return (
                                                                <th key={field.id} style={{ width: field.width !== 'auto' ? field.width : undefined }}>
                                                                    <div className="header-with-copy">
                                                                        <span className="header-text">{field.name}</span>
                                                                        {hasValue && (
                                                                            <button
                                                                                className="copy-from-last-ddr-btn header-copy-btn"
                                                                                onClick={() => handleCopyFromLastDDRToTargetRow(field, value, currentRowIndex)}
                                                                                title={`Copy "${field.name}" to last editing row`}
                                                                            >
                                                                                📋
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </th>
                                                            );
                                                        })
                                                    }
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Show only the current row */}
                                                {lastDDRData.length > 0 && (
                                                    <tr>
                                                        {config.fields
                                                            .filter(field => field.enabled)
                                                            .map(field => (
                                                                <td key={field.id}>
                                                                    <MemoizedFieldValue
                                                                        value={lastDDRData[currentRowIndex][field.dbColumn]}
                                                                        field={field}
                                                                        globalSettings={globalSettings}
                                                                    />
                                                                </td>
                                                            ))
                                                        }
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                // Form view - render exactly like main DDR section but with copy buttons
                                <div className="last-ddr-section-container">
                                    <div className="form-container">
                                        {(() => {
                                            const dataRecord = lastDDRData[0] || {};
                                            const enabledFields = config.fields.filter(field => field.enabled);
                                            
                                            // Group fields by their group property
                                            const fieldGroups = enabledFields.reduce((groups, field) => {
                                                const groupId = field.group || 1;
                                                if (!groups[groupId]) {
                                                    groups[groupId] = [];
                                                }
                                                groups[groupId].push(field);
                                                return groups;
                                            }, {});

                                            // Get group settings with fallback
                                            const groupSettings = config.groupSettings || {
                                                1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
                                            };

                                            return Object.entries(fieldGroups)
                                                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                                .map(([groupId, fields]) => {
                                                    const settings = groupSettings[groupId] || { itemsPerRow: 2, customCSS: '', name: `Group ${groupId}`, showGroupName: false };
                                                    const groupClassName = `field-group-${config.id}-${groupId}-last-ddr`;

                                                    return (
                                                        <div key={groupId}
                                                            className={`field-group ${groupClassName}`}
                                                            style={{
                                                                gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                                                ...(settings.customCSS && {
                                                                    background: 'unset',
                                                                    padding: 'unset',
                                                                    border: 'unset',
                                                                    borderRadius: 'unset',
                                                                    margin: 'unset'
                                                                })
                                                            }}>
                                                            
                                                            {/* Add scoped custom CSS if provided */}
                                                            {settings.customCSS && (
                                                                <style>
                                                                    {settings.customCSS
                                                                        .replace(/\.field-group/g, `.${groupClassName}`)
                                                                        .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                                                                            const importantRules = rules.replace(
                                                                                /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                                                                                '$1$2 !important'
                                                                            );
                                                                            return `${selector}{${importantRules}}`;
                                                                        })
                                                                    }
                                                                </style>
                                                            )}

                                                            {/* Show group title based on showGroupName setting */}
                                                            {settings.showGroupName && (
                                                                <div className="field-group-title">{settings.name}</div>
                                                            )}

                                                            {/* Check if this is an Excel-style template that needs table structure */}
                                                            {settings.customCSS && settings.customCSS.includes('.field-grid') ? (
                                                                // Excel table structure
                                                                <div className="field-grid"
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                                                        gap: '0'
                                                                    }}>
                                                                    {fields.map(field => (
                                                                        field.width === 'full' ? (
                                                                            <div key={field.id}
                                                                                className="field-row"
                                                                                style={{
                                                                                    gridColumn: '1 / -1',
                                                                                    display: 'grid',
                                                                                    gridTemplateColumns: '1fr 2fr auto'
                                                                                }}>
                                                                                <div className="label">{field.name}</div>
                                                                                <div className="value">
                                                                                    <MemoizedFieldValue
                                                                                        value={dataRecord[field.dbColumn]}
                                                                                        field={field}
                                                                                        globalSettings={globalSettings}
                                                                                    />
                                                                                </div>
                                                                                <div className="copy-action">
                                                                                    {dataRecord[field.dbColumn] !== null && dataRecord[field.dbColumn] !== undefined && dataRecord[field.dbColumn] !== '' && (
                                                                                        <button
                                                                                            className="copy-from-last-ddr-btn"
                                                                                            onClick={() => handleCopyFromLastDDRToTargetRow(field, dataRecord[field.dbColumn])}
                                                                                            title={`Copy "${field.name}"`}
                                                                                        >
                                                                                            📋
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div key={field.id} className="field-row">
                                                                                <div className="label">{field.name}</div>
                                                                                <div className="value">
                                                                                    <MemoizedFieldValue
                                                                                        value={dataRecord[field.dbColumn]}
                                                                                        field={field}
                                                                                        globalSettings={globalSettings}
                                                                                    />
                                                                                </div>
                                                                                <div className="copy-action">
                                                                                    {dataRecord[field.dbColumn] !== null && dataRecord[field.dbColumn] !== undefined && dataRecord[field.dbColumn] !== '' && (
                                                                                        <button
                                                                                            className="copy-from-last-ddr-btn"
                                                                                            onClick={() => handleCopyFromLastDDRToTargetRow(field, dataRecord[field.dbColumn])}
                                                                                            title={`Copy "${field.name}"`}
                                                                                        >
                                                                                            📋
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                // Standard grid structure
                                                                <div className="field-group-content"
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                                                                        gap: '16px'
                                                                    }}>
                                                                    {fields.map(field => (
                                                                        <div key={field.id}
                                                                            className={`label-value-item last-ddr-label-value-item ${field.width === 'full' ? 'field-width-full' : ''}`}
                                                                            style={field.width === 'full' ? { gridColumn: '1 / -1' } : {}}>
                                                                            <div className="label-with-copy">
                                                                                <span className="label">{field.name}</span>
                                                                                {dataRecord[field.dbColumn] !== null && dataRecord[field.dbColumn] !== undefined && dataRecord[field.dbColumn] !== '' && (
                                                                                    <button
                                                                                        className="copy-from-last-ddr-btn"
                                                                                        onClick={() => handleCopyFromLastDDRToTargetRow(field, dataRecord[field.dbColumn])}
                                                                                        title={`Copy "${field.name}"`}
                                                                                    >
                                                                                        📋
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                            <span className="value">
                                                                                <MemoizedFieldValue
                                                                                    value={dataRecord[field.dbColumn]}
                                                                                    field={field}
                                                                                    globalSettings={globalSettings}
                                                                                />
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                        })()}
                                    </div>
                                </div>
                            )
                        ) : (
                            // No data in this table but report exists
                            <div className="last-ddr-no-data">
                                <div className="no-data-icon">📋</div>
                                <p>Previous report found, but no data available in the <strong>{config.name}</strong> section.</p>
                                {lastDDRInfo?.message && (
                                    <p className="no-data-details">{lastDDRInfo.message}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
});

const DDR_v2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Core state - simplified and optimized
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState(params.get('wellId') || '');
    const [reports, setReports] = useState([]);
    const [currentReport, setCurrentReport] = useState(null);
    const [currentReportId, setCurrentReportId] = useState(null);
    const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
    const [isNewReport, setIsNewReport] = useState(false);

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [isControlsMinimized, setIsControlsMinimized] = useState(false);

    // Layout and data state
    const [layoutConfig, setLayoutConfig] = useState(null);
    const [sectionData, setSectionData] = useState({});
    const [editingData, setEditingData] = useState({});
    const [editingSections, setEditingSections] = useState({});
    const [savingStates, setSavingStates] = useState({});
    const [globalSettings, setGlobalSettings] = useState({});

    // Modal states
    const [showNewReportModal, setShowNewReportModal] = useState(false);
    const [newReportDate, setNewReportDate] = useState(new Date().toISOString().split('T')[0]);
    const [isCreatingReport, setIsCreatingReport] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeletingReport, setIsDeletingReport] = useState(false);

    // Header and styling state
    const [processedHeaderHTML, setProcessedHeaderHTML] = useState('');

    // PDF Export hook
    const { 
        quickExportToPDF, 
        validateExportReadiness, 
        createPDFContentRenderer,
        isExporting, 
        exportError, 
        clearExportError 
    } = useDDRPDFExport();

    // DataForm state
    const [showDataForm, setShowDataForm] = useState(false);
    const [dataFormConfig, setDataFormConfig] = useState(null);
    const [editAttempts, setEditAttempts] = useState({}); // Track edit attempts per section

    // Section navigation state
    const [collapsedSections, setCollapsedSections] = useState({});
    const sectionRefs = useRef({});

    // Global context menu state
    const [globalContextMenu, setGlobalContextMenu] = useState({ 
        visible: false, 
        x: 0, 
        y: 0, 
        sectionId: null 
    });

    const initialWellLoaded = useRef(false);
    const role = localStorage.getItem('role') || 'admin';
    const calendarRef = useRef(null);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showCalendar]);





    // Optimized data loading functions
    const loadSectionData = useCallback(async (sectionConfig, wellId, reportId, reportDate) => {
        if (!sectionConfig.tableName || !wellId) return {};

        try {
            let url = `/api/db/${sectionConfig.tableName}`;
            
            switch (sectionConfig.keyColumn) {
                case 'wellId':
                    url += `?filter=WellID,eq,${wellId}`;
                    break;
                case 'reportId':
                    if (!reportId) return {};
                    url += `?filter=ReportID,eq,${reportId}`;
                    break;
                case 'wellId+date':
                    if (!reportDate) return {};
                    url += `?filter=WellID,eq,${wellId}&filter=ReportDate,eq,${reportDate}`;
                    break;
                default:
                    return {};
            }

            const response = await apiFetch(url);
            return Array.isArray(response) ? response : [];
        } catch (err) {
            console.error(`Failed to load ${sectionConfig.tableName} data:`, err);
            return {};
        }
    }, []);

    const loadAllSectionData = useCallback(async (wellId, reportId, reportDate, forceRefresh = false) => {
        if (!layoutConfig) return;

        if (forceRefresh) {
            setSectionData({});
        }

        const enabledSections = layoutConfig.sections.filter(section => section.enabled && section.tableName);
        const newSectionData = {};

        const promises = enabledSections.map(async (section) => {
            try {
                const data = await loadSectionData(section, wellId, reportId, reportDate);
                newSectionData[section.id] = data;
            } catch (error) {
                console.error(`Failed to load section ${section.id}:`, error);
                newSectionData[section.id] = [];
            }
        });

        await Promise.all(promises);
        setSectionData(newSectionData);
    }, [layoutConfig, loadSectionData]);

    // Get primary key for table with hardcoded mappings
    const getPrimaryKey = useCallback((tableName) => {
        const primaryKeys = {
            'Well': 'WellID',
            'DailyWellData': 'DailyID',
            'DailyActivity': 'ActivityID',
            'DailyPOB': 'AssignmentID',
            'DailyCasing': 'CasingActivityID',
            'DailyPerforation': 'PerforationID',
            'MudProperties': 'MudID',
            'Weather': 'WeatherID',
            'POB': 'POBID',
            'WellPlan': 'PlanID',
            'WellLog': 'LogID',
            'BHA': 'BHAID',
            'BitRecord': 'BitID',
            'BulkMaterial': 'MaterialID',
            'Attachment': 'AttachmentID'
        };
        return primaryKeys[tableName] || tableName + 'ID';
    }, []);

    // Clean payload for database operations
    const cleanPayloadForDatabase = useCallback((payload, tableName, primaryKey, isUpdate = false) => {
        const cleaned = { ...payload };
        
        // For updates, convert empty strings to null to clear fields
        // For creates, remove null/undefined/empty values
        Object.keys(cleaned).forEach(key => {
            const value = cleaned[key];
            if (value === null || value === undefined) {
                if (isUpdate) {
                    cleaned[key] = null; // Keep null for updates
                } else {
                    delete cleaned[key]; // Remove for creates
                }
            } else if (value === '') {
                if (isUpdate) {
                    cleaned[key] = null; // Convert empty string to null for updates
                } else {
                    delete cleaned[key]; // Remove empty strings for creates
                }
            }
        });

        // Ensure foreign keys are properly typed as integers
        if (cleaned.WellID) cleaned.WellID = parseInt(cleaned.WellID);
        if (cleaned.ReportID) cleaned.ReportID = parseInt(cleaned.ReportID);

        // Remove timestamp fields
        ['CreatedDate', 'ModifiedDate', 'CreatedBy', 'ModifiedBy', 'timestamp'].forEach(field => {
            delete cleaned[field];
        });

        return cleaned;
    }, []);

    // Optimized save function with proper state management
    const saveSectionData = useCallback(async (sectionId, overrideEditData = null) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        if (!sectionConfig) return;

        // Prevent multiple simultaneous saves
        if (savingStates[sectionId]) return;

        setSavingStates(prev => ({ ...prev, [sectionId]: true }));

        try {
            const editData = overrideEditData !== null ? overrideEditData : editingData[sectionId];
            if (!editData) throw new Error('No editing data found');

            const primaryKey = getPrimaryKey(sectionConfig.tableName);
            const originalData = sectionData[sectionId] || {};

            console.log('💾 saveSectionData details:', {
                sectionId,
                sectionName: sectionConfig.name,
                tableName: sectionConfig.tableName,
                primaryKey,
                editData,
                originalData,
                keyColumn: sectionConfig.keyColumn,
                presentationType: sectionConfig.presentationType
            });

            if (sectionConfig.presentationType === 'table') {
                // Handle table data with proper CRUD operations
                const tableRows = editData.tableRows || [];
                const originalRows = Array.isArray(originalData) ? originalData : [];
                
                console.log('💾 Table save operation details:', {
                    tableRows: tableRows.length,
                    originalRows: originalRows.length,
                    primaryKey,
                    tableRowsData: tableRows,
                    originalRowsData: originalRows
                });
                
                // Create a map of original records by primary key for easy lookup
                const originalRecordsMap = new Map();
                originalRows.forEach(row => {
                    if (row[primaryKey]) {
                        originalRecordsMap.set(row[primaryKey], row);
                    }
                });
                
                // Track which original records are still present in the new data
                const updatedRecordIds = new Set();
                
                // Save/update current records
                for (let i = 0; i < tableRows.length; i++) {
                    const row = tableRows[i];
                    let cleanRow = { ...row };
                    
                    console.log(`💾 Processing row ${i + 1}/${tableRows.length}:`, row);
                    
                    // Add required foreign keys
                    switch (sectionConfig.keyColumn) {
                        case 'wellId':
                            cleanRow.WellID = parseInt(selectedWellId);
                            break;
                        case 'reportId':
                            cleanRow.ReportID = currentReportId;
                            break;
                        case 'wellId+date':
                            cleanRow.WellID = parseInt(selectedWellId);
                            cleanRow.ReportDate = reportDate;
                            break;
                    }

                    const recordId = row[primaryKey];
                    const isUpdate = recordId && recordId > 0 && originalRecordsMap.has(recordId);
                    
                    console.log(`💾 Row ${i + 1} operation:`, { recordId, isUpdate, hasOriginal: originalRecordsMap.has(recordId) });

                    if (isUpdate) {
                        // Update existing record
                        updatedRecordIds.add(recordId);
                        
                        // Clean payload but keep the primary key for comparison
                        const cleanedForUpdate = cleanPayloadForDatabase(cleanRow, sectionConfig.tableName, primaryKey, true);
                        delete cleanedForUpdate[primaryKey]; // Remove primary key for update API call
                        
                        console.log(`💾 Updating record ${recordId}:`, cleanedForUpdate);
                        
                        await apiFetch(`/api/db/${sectionConfig.tableName}/${recordId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(cleanedForUpdate)
                        });
                        
                        console.log(`✅ Record ${recordId} updated successfully`);
                    } else {
                        // Create new record
                        const cleanedForCreate = cleanPayloadForDatabase(cleanRow, sectionConfig.tableName, primaryKey, false);
                        delete cleanedForCreate[primaryKey]; // Let database generate ID
                        
                        console.log(`💾 Creating new record:`, cleanedForCreate);
                        
                        const response = await apiFetch(`/api/db/${sectionConfig.tableName}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(cleanedForCreate)
                        });
                        
                        console.log(`✅ New record created:`, response);
                    }
                }
                
                // Delete records that were removed (only delete records that existed originally but are not in the updated set)
                const recordsToDelete = Array.from(originalRecordsMap.keys()).filter(id => !updatedRecordIds.has(id));
                
                console.log('💾 Records to delete:', recordsToDelete);
                
                for (const recordId of recordsToDelete) {
                    console.log(`🗑️ Deleting record ${recordId}`);
                    await apiFetch(`/api/db/${sectionConfig.tableName}/${recordId}`, {
                        method: 'DELETE'
                    });
                    console.log(`✅ Record ${recordId} deleted successfully`);
                }
                
                console.log('💾 Table save operation completed');
            } else {
                // Handle form data
                let payload = { ...editData };
                
                console.log('💾 Form data before foreign keys:', payload);
                
                // Add required foreign keys
                switch (sectionConfig.keyColumn) {
                    case 'wellId':
                        payload.WellID = parseInt(selectedWellId);
                        break;
                    case 'reportId':
                        payload.ReportID = currentReportId;
                        break;
                    case 'wellId+date':
                        payload.WellID = parseInt(selectedWellId);
                        payload.ReportDate = reportDate;
                        break;
                }

                console.log('💾 Form data after foreign keys:', payload);

                // Determine if this is create or update first
                const recordId = payload[primaryKey];
                const isUpdate = recordId && recordId > 0;

                console.log('💾 Operation decision:', { primaryKey, recordId, isUpdate });

                // Clean payload with appropriate mode
                payload = cleanPayloadForDatabase(payload, sectionConfig.tableName, primaryKey, isUpdate);

                console.log('💾 Form data after cleaning:', payload);

                if (isUpdate) {
                    // Update existing record
                    const updatePayload = { ...payload };
                    delete updatePayload[primaryKey]; // Remove primary key for update
                    
                    console.log('💾 Updating record:', { recordId, updatePayload });
                    
                    await apiFetch(`/api/db/${sectionConfig.tableName}/${recordId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(updatePayload)
                    });
                } else {
                    // Create new record
                    const createPayload = { ...payload };
                    delete createPayload[primaryKey]; // Let database generate ID
                    
                    console.log('💾 Creating new record:', { createPayload });
                    
                    const response = await apiFetch(`/api/db/${sectionConfig.tableName}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(createPayload)
                    });
                    
                    console.log('💾 Create response:', response);
                }
            }

            // Show success toast IMMEDIATELY
            setToast(`✅ ${sectionConfig.name} saved successfully`);

            // Clear editing state synchronously
            ReactDOM.flushSync(() => {
                setEditingSections(prev => ({ ...prev, [sectionId]: false }));
                setEditingData(prev => {
                    const newData = { ...prev };
                    delete newData[sectionId];
                    return newData;
                });
            });

            // Reload only this section's data
            const updatedData = await loadSectionData(
                sectionConfig, 
                selectedWellId, 
                currentReportId, 
                reportDate
            );
            
            setSectionData(prev => ({
                ...prev,
                [sectionId]: updatedData
            }));

        } catch (err) {
            console.error(`Failed to save ${sectionConfig.name}:`, err);
            setToast(`❌ Failed to save ${sectionConfig.name}: ${err.message}`);
        } finally {
            setSavingStates(prev => {
                const newState = { ...prev };
                delete newState[sectionId];
                return newState;
            });
        }
    }, [layoutConfig, editingData, savingStates, selectedWellId, currentReportId, reportDate, loadSectionData, sectionData, getPrimaryKey, cleanPayloadForDatabase]);

    // Section management functions
    const handleEditSection = useCallback((sectionId) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        if (!sectionConfig) return;

        const currentData = sectionData[sectionId] || {};
        let initialEditData;

        if (sectionConfig.presentationType === 'table') {
            initialEditData = {
                tableRows: Array.isArray(currentData) ? [...currentData] : []
            };
        } else {
            initialEditData = Array.isArray(currentData) && currentData.length > 0 
                ? { ...currentData[0] } 
                : {};
        }

        setEditingData(prev => ({ ...prev, [sectionId]: initialEditData }));
        setEditingSections(prev => ({ ...prev, [sectionId]: true }));

        // Perform initial calculations for any calculated fields
        setTimeout(() => {
            if (sectionConfig.fields) {
                const calculatedFields = sectionConfig.fields.filter(field => isCalculatedField(field));
                
                if (calculatedFields.length > 0) {
                    console.log('🧮 Performing initial calculations for section:', sectionId, calculatedFields);
                    
                    if (sectionConfig.presentationType === 'table') {
                        // For table sections, calculate for each row
                        const tableRows = initialEditData.tableRows || [];
                        tableRows.forEach((row, rowIndex) => {
                            calculatedFields.forEach(field => {
                                if (field.calculation) {
                                    const calculatedValue = evaluateCalculation(field.calculation, row);
                                    if (calculatedValue !== '' && calculatedValue !== null && calculatedValue !== undefined) {
                                        setEditingData(prevData => {
                                            const prevSectionData = prevData[sectionId] || {};
                                            const prevTableRows = [...(prevSectionData.tableRows || [])];
                                            if (prevTableRows[rowIndex]) {
                                                prevTableRows[rowIndex] = { 
                                                    ...prevTableRows[rowIndex], 
                                                    [field.dbColumn]: calculatedValue 
                                                };
                                            }
                                            return { 
                                                ...prevData, 
                                                [sectionId]: { ...prevSectionData, tableRows: prevTableRows } 
                                            };
                                        });
                                    }
                                }
                            });
                        });
                    } else {
                        // For form sections, calculate once
                        calculatedFields.forEach(field => {
                            if (field.calculation) {
                                const calculatedValue = evaluateCalculation(field.calculation, initialEditData);
                                if (calculatedValue !== '' && calculatedValue !== null && calculatedValue !== undefined) {
                                    setEditingData(prevData => {
                                        const prevSectionData = prevData[sectionId] || {};
                                        return { 
                                            ...prevData, 
                                            [sectionId]: { ...prevSectionData, [field.dbColumn]: calculatedValue } 
                                        };
                                    });
                                }
                            }
                        });
                    }
                }
            }
        }, 100); // Small delay to allow state to update
    }, [layoutConfig, sectionData]);

    const handleCancelEdit = useCallback((sectionId) => {
        setEditingSections(prev => ({ ...prev, [sectionId]: false }));
        setEditingData(prev => {
            const newData = { ...prev };
            delete newData[sectionId];
            return newData;
        });
        setToast(`🚫 Changes cancelled for section`);
    }, []);

    // Function to exit all edit modes
    const exitAllEditModes = useCallback(() => {
        const activelySectionIds = Object.keys(editingSections).filter(id => editingSections[id]);
        
        if (activelySectionIds.length > 0) {
            console.log('🚫 Exiting edit mode for all sections:', activelySectionIds);
            
            // Clear all editing states
            setEditingSections({});
            setEditingData({});
            
            // Show toast notification
            setToast(`🚫 Edit mode exited for ${activelySectionIds.length} section${activelySectionIds.length > 1 ? 's' : ''}`);
        }
    }, [editingSections]);

    const handleFieldChange = useCallback((sectionId, fieldColumn, value, rowIndex = null) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        
        setEditingData(prev => {
            const sectionData = prev[sectionId] || {};
            let newSectionData;
            
            if (rowIndex !== null) {
                // Table field change
                const tableRows = [...(sectionData.tableRows || [])];
                if (tableRows[rowIndex]) {
                    tableRows[rowIndex] = { ...tableRows[rowIndex], [fieldColumn]: value };
                }
                newSectionData = { ...sectionData, tableRows };
            } else {
                // Form field change
                newSectionData = { ...sectionData, [fieldColumn]: value };
            }
            
            const updatedData = { ...prev, [sectionId]: newSectionData };
            
            // Trigger recalculation for dependent fields
            if (sectionConfig?.fields) {
                const currentFormData = rowIndex !== null 
                    ? newSectionData.tableRows?.[rowIndex] || {}
                    : newSectionData;
                
                // Use setTimeout to avoid blocking the UI and allow state to update
                setTimeout(() => {
                    recalculateFields(
                        fieldColumn,
                        currentFormData,
                        sectionConfig.fields,
                        (calculatedFieldColumn, calculatedValue, calcRowIndex) => {
                            // Recursive call to update calculated field
                            setEditingData(prevData => {
                                const prevSectionData = prevData[sectionId] || {};
                                
                                if (calcRowIndex !== null) {
                                    // Update table row
                                    const prevTableRows = [...(prevSectionData.tableRows || [])];
                                    if (prevTableRows[calcRowIndex]) {
                                        prevTableRows[calcRowIndex] = { 
                                            ...prevTableRows[calcRowIndex], 
                                            [calculatedFieldColumn]: calculatedValue 
                                        };
                                    }
                                    return { 
                                        ...prevData, 
                                        [sectionId]: { ...prevSectionData, tableRows: prevTableRows } 
                                    };
                                } else {
                                    // Update form field
                                    return { 
                                        ...prevData, 
                                        [sectionId]: { ...prevSectionData, [calculatedFieldColumn]: calculatedValue } 
                                    };
                                }
                            });
                        },
                        rowIndex
                    );
                }, 0);
            }
            
            return updatedData;
        });
    }, [layoutConfig]);

    // Copy value from Last DDR to editing form
    const handleCopyFromLastDDR = useCallback((sectionId, field, value, rowIndex = null) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        if (!sectionConfig) return;

        // Get current value to check if field is empty
        const currentEditingData = editingData[sectionId] || {};
        let currentValue;
        
        if (rowIndex !== null) {
            // Table field
            const tableRows = currentEditingData.tableRows || [];
            currentValue = tableRows[rowIndex]?.[field.dbColumn];
        } else {
            // Form field
            currentValue = currentEditingData[field.dbColumn];
        }

        // Check if current field has a value and needs confirmation
        const hasCurrentValue = currentValue !== null && currentValue !== undefined && currentValue !== '';
        
        const performCopy = () => {
            handleFieldChange(sectionId, field.dbColumn, value, rowIndex);
            
            // Show success toast
            setToast(`✅ Copied "${field.name}" from last DDR`);
            setTimeout(() => setToast(''), 3000);
        };

        if (hasCurrentValue) {
            // Show confirmation dialog
            const confirmed = window.confirm(
                `The field "${field.name}" already contains a value.\n\n` +
                `Current value: ${currentValue}\n` +
                `New value from last DDR: ${value}\n\n` +
                `Do you want to replace the current value?`
            );
            
            if (confirmed) {
                performCopy();
            }
        } else {
            // Field is empty, copy directly
            performCopy();
        }
    }, [layoutConfig, editingData, handleFieldChange]);

    // Multi-field change handler for lookup fields with lookupReturnColumn
    const handleMultiFieldChange = useCallback((sectionId, fieldMappings, rowIndex = null) => {
        console.log('🔄 DDR_v2: Multi-field change:', { sectionId, fieldMappings, rowIndex });
        
        setEditingData(prev => {
            const sectionData = prev[sectionId] || {};
            
            if (rowIndex !== null) {
                // Table multi-field change
                const tableRows = [...(sectionData.tableRows || [])];
                if (tableRows[rowIndex]) {
                    tableRows[rowIndex] = { ...tableRows[rowIndex], ...fieldMappings };
                }
                return { ...prev, [sectionId]: { ...sectionData, tableRows } };
            } else {
                // Form multi-field change
                return { ...prev, [sectionId]: { ...sectionData, ...fieldMappings } };
            }
        });
    }, []);

    const handleAddRow = useCallback((sectionId) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        if (!sectionConfig) return;

        const emptyRow = {};
        sectionConfig.fields.forEach(field => {
            if (field.enabled && field.dbColumn) {
                emptyRow[field.dbColumn] = '';
            }
        });

        setEditingData(prev => {
            const sectionData = prev[sectionId] || {};
            const tableRows = [...(sectionData.tableRows || [])];

            // Special logic for Operations Summary: set starttime = endtime of previous row
            if (sectionId === 'operationsSummary' && tableRows.length > 0) {
                const prevRow = tableRows[tableRows.length - 1];
                if (prevRow && prevRow.EndTime) {
                    emptyRow.StartTime = prevRow.EndTime;
                }
            }

            tableRows.push(emptyRow);
            return { ...prev, [sectionId]: { ...sectionData, tableRows } };
        });
    }, [layoutConfig]);

    const handleRemoveRow = useCallback((sectionId, rowIndex) => {
        setEditingData(prev => {
            const sectionData = prev[sectionId] || {};
            const tableRows = (sectionData.tableRows || []).filter((_, index) => index !== rowIndex);
            return { ...prev, [sectionId]: { ...sectionData, tableRows } };
        });
    }, []);

    // DataForm handlers
    const handleEditForm = useCallback(async (sectionId) => {
        const sectionConfig = layoutConfig?.sections.find(s => s.id === sectionId);
        if (!sectionConfig) return;

        // Track edit attempts to detect alternating pattern
        setEditAttempts(prev => ({
            ...prev,
            [sectionId]: (prev[sectionId] || 0) + 1
        }));

        console.log('🔧 Opening DataForm for section:', {
            sectionId,
            sectionName: sectionConfig.name,
            tableName: sectionConfig.tableName,
            presentationType: sectionConfig.presentationType,
            currentSectionData: sectionData[sectionId],
            editAttempt: editAttempts[sectionId] + 1 || 1
        });

        // Always fetch fresh data to avoid stale state issues
        let freshData;
        try {
            freshData = await loadSectionData(
                sectionConfig, 
                selectedWellId, 
                currentReportId, 
                reportDate
            );
            console.log('🔧 Fresh data loaded for DataForm:', freshData);
        } catch (error) {
            console.error('Failed to load fresh data for DataForm:', error);
            // Fallback to existing data
            freshData = sectionData[sectionId] || {};
        }

        // Determine view mode and prepare data based on section type
        let viewMode = 'form';
        let formData = {};
        let tableData = [];

        if (sectionConfig.presentationType === 'table') {
            // For table sections, use table view mode
            viewMode = 'table';
            tableData = Array.isArray(freshData) ? freshData : [];
            // Also prepare single record for fallback
            formData = Array.isArray(freshData) && freshData.length > 0 ? freshData[0] : {};
        } else {
            // For form sections, use form view mode
            viewMode = 'form';
            formData = Array.isArray(freshData) && freshData.length > 0 ? freshData[0] : {};
        }

        console.log('🔧 Final DataForm configuration:', {
            sectionId,
            sectionName: sectionConfig.name,
            tableName: sectionConfig.tableName,
            viewMode,
            formData,
            tableData: tableData.length > 0 ? `${tableData.length} records` : 'empty',
            presentationType: sectionConfig.presentationType
        });

        // Set up DataForm configuration
        setDataFormConfig({
            sectionConfig,
            tableName: sectionConfig.tableName,
            data: formData,
            tableData: tableData,
            viewMode: viewMode
        });

        setShowDataForm(true);
    }, [layoutConfig, sectionData, loadSectionData, selectedWellId, currentReportId, reportDate]);

    const handleDataFormSave = useCallback(async (data) => {
        if (!dataFormConfig?.sectionConfig) {
            console.error('No section config available for DataForm save');
            setToast('❌ No section configuration available');
            return;
        }

        const saveStartTime = Date.now();
        console.log('💾 Starting DataForm save cycle:', {
            saveStartTime,
            sectionConfig: dataFormConfig.sectionConfig.name,
            tableName: dataFormConfig.tableName,
            viewMode: dataFormConfig.viewMode,
            data,
            selectedWellId,
            currentReportId,
            reportDate,
            currentSectionData: sectionData[dataFormConfig.sectionConfig.id]
        });

        try {
            // Use the existing save function with proper data structure
            const sectionConfig = dataFormConfig.sectionConfig;
            const sectionId = sectionConfig.id;

            let editData;

            if (dataFormConfig.viewMode === 'table') {
                // For table view mode, data is an array of records
                console.log('💾 Processing table view data:', data);
                
                // Enrich each record with foreign keys
                const enrichedTableData = Array.isArray(data) ? data.map(record => {
                    let enrichedRecord = { ...record };
                    
                    switch (sectionConfig.keyColumn) {
                        case 'wellId':
                            enrichedRecord.WellID = parseInt(selectedWellId);
                            break;
                        case 'reportId':
                            enrichedRecord.ReportID = currentReportId;
                            break;
                        case 'wellId+date':
                            enrichedRecord.WellID = parseInt(selectedWellId);
                            enrichedRecord.ReportDate = reportDate;
                            break;
                    }
                    
                    return enrichedRecord;
                }) : [];

                console.log('💾 Enriched table data with foreign keys:', enrichedTableData);

                // Prepare editing data for table sections
                editData = { tableRows: enrichedTableData };
            } else {
                // For form view mode, data is a single record
                let enrichedData = { ...data };
                
                switch (sectionConfig.keyColumn) {
                    case 'wellId':
                        enrichedData.WellID = parseInt(selectedWellId);
                        break;
                    case 'reportId':
                        enrichedData.ReportID = currentReportId;
                        break;
                    case 'wellId+date':
                        enrichedData.WellID = parseInt(selectedWellId);
                        enrichedData.ReportDate = reportDate;
                        break;
                }

                console.log('💾 Enriched form data with foreign keys:', enrichedData);

                // Prepare editing data for form sections
                editData = sectionConfig.presentationType === 'table' 
                    ? { tableRows: [enrichedData] } 
                    : enrichedData;
            }

            console.log('💾 Setting editing data for save:', { sectionId, editData, viewMode: dataFormConfig.viewMode });

            // Clear any existing editing data for this section first, then set new data
            setEditingData(prev => {
                const newEditingData = { ...prev };
                delete newEditingData[sectionId]; // Clear existing
                newEditingData[sectionId] = editData; // Set new
                return newEditingData;
            });

            // Save the data using existing save function
            await saveSectionData(sectionId, editData);

            // Wait a moment for the section data to be refreshed
            await new Promise(resolve => setTimeout(resolve, 100));

            // Close the DataForm only after successful save
            setShowDataForm(false);
            setDataFormConfig(null);

            const saveEndTime = Date.now();
            console.log('✅ DataForm save cycle completed:', {
                saveStartTime,
                saveEndTime,
                duration: saveEndTime - saveStartTime,
                sectionId,
                viewMode: dataFormConfig.viewMode,
                updatedSectionData: sectionData[sectionId]
            });

        } catch (error) {
            console.error('Failed to save DataForm data:', error);
            setToast(`❌ Failed to save ${dataFormConfig?.sectionConfig?.name || 'section'} data: ${error.message}`);
        }
    }, [dataFormConfig, saveSectionData, setToast, selectedWellId, currentReportId, reportDate]);

    const handleDataFormCancel = useCallback(() => {
        setShowDataForm(false);
        setDataFormConfig(null);
    }, []);

    // Global context menu handlers
    const handleGlobalContextMenu = useCallback((e, sectionId) => {
        e.preventDefault();
        
        // For fixed positioning, use client coordinates (viewport relative)
        const x = e.clientX;
        const y = e.clientY;
        
        console.log('🎯 Global context menu triggered:', { 
            clientX: e.clientX, 
            clientY: e.clientY,
            pageX: e.pageX,
            pageY: e.pageY,
            scrollX: window.pageXOffset,
            scrollY: window.pageYOffset,
            finalX: x,
            finalY: y,
            sectionId 
        });
        
        setGlobalContextMenu({
            visible: true,
            x: x,
            y: y,
            sectionId: sectionId
        });
    }, []);

    const handleCloseGlobalContextMenu = useCallback(() => {
        setGlobalContextMenu({ visible: false, x: 0, y: 0, sectionId: null });
    }, []);

    const handleGlobalEditInline = useCallback(() => {
        if (globalContextMenu.sectionId) {
            handleEditSection(globalContextMenu.sectionId);
            handleCloseGlobalContextMenu();
        }
    }, [globalContextMenu.sectionId, handleEditSection, handleCloseGlobalContextMenu]);

    const handleGlobalEditForm = useCallback(() => {
        if (globalContextMenu.sectionId) {
            handleEditForm(globalContextMenu.sectionId);
            handleCloseGlobalContextMenu();
        }
    }, [globalContextMenu.sectionId, handleEditForm, handleCloseGlobalContextMenu]);

    // Global context menu options
    const globalContextMenuOptions = useMemo(() => {
        const currentSection = layoutConfig?.sections?.find(s => s.id === globalContextMenu.sectionId);
        return [
            {
                type: 'header',
                label: currentSection?.name || 'Section',
                icon: '📋'
            },
            {
                type: 'divider'
            },
            {
                type: 'action',
                label: 'Edit Inline',
                icon: '✏️',
                onClick: handleGlobalEditInline
            },
            {
                type: 'action',
                label: 'Edit on Form',
                icon: '📝',
                onClick: handleGlobalEditForm
            }
        ];
    }, [globalContextMenu.sectionId, layoutConfig?.sections, handleGlobalEditInline, handleGlobalEditForm]);

    // Well and report management
    const handleWellChange = useCallback(async (wellId) => {
        // Exit all edit modes before changing well
        exitAllEditModes();
        
        setSelectedWellId(wellId);
        setCurrentReport(null);
        setCurrentReportId(null);
        setSectionData({});
        setEditingData({});
        setEditingSections({});
        setEditAttempts({}); // Clear edit attempts when changing wells

        if (wellId) {
            setIsLoading(true);
            try {
                const reportsData = await apiFetch(`/api/ddr?wellId=${wellId}`);
                const sortedReports = Array.isArray(reportsData) 
                    ? reportsData.sort((a, b) => new Date(b.ReportDate) - new Date(a.ReportDate))
                    : [];

                setReports(sortedReports);

                if (sortedReports.length > 0) {
                    const latestReport = sortedReports[0];
                    setCurrentReport(latestReport);
                    setCurrentReportId(latestReport.ReportID);
                    setReportDate(latestReport.ReportDate.split('T')[0]);
                    
                    await loadAllSectionData(wellId, latestReport.ReportID, latestReport.ReportDate.split('T')[0]);
                    
                                navigate(`/ddr?wellId=${wellId}&reportId=${latestReport.ReportID}`, { replace: true });
        } else {
            navigate(`/ddr?wellId=${wellId}`, { replace: true });
                }
            } catch (err) {
                console.error('Failed to load reports:', err);
                setToast('Failed to load reports');
            } finally {
                setIsLoading(false);
            }
        } else {
            setReports([]);
            navigate('/ddr', { replace: true });
        }
    }, [exitAllEditModes, navigate, loadAllSectionData]);

    const handleDateSelect = useCallback(async (date) => {
        if (!date) {
            console.warn('No date provided to handleDateSelect');
            return;
        }

        console.log('📅 Date selected:', date);
        
        // Exit all edit modes before changing date
        exitAllEditModes();
        
        const selectedReport = reports.find(r => isSameDate(r.ReportDate, date));
        
        if (selectedReport) {
            console.log('✅ Found existing report for date:', selectedReport);
            setCurrentReport(selectedReport);
            setCurrentReportId(selectedReport.ReportID);
            setReportDate(selectedReport.ReportDate.split('T')[0]);
            
            await loadAllSectionData(selectedWellId, selectedReport.ReportID, selectedReport.ReportDate.split('T')[0]);
            navigate(`/ddr?wellId=${selectedWellId}&reportId=${selectedReport.ReportID}`);
            setShowCalendar(false);
        } else {
            console.log('⚠️ No report found for selected date, creating new report...');
            // If no report exists for this date, ask user if they want to create one
            const dateString = date.toISOString().split('T')[0];
            const confirmCreate = window.confirm(
                `No report exists for ${formatDate(date)}. Would you like to create a new report for this date?`
            );
            
            if (confirmCreate) {
                try {
                    setIsCreatingReport(true);
                    const reportPayload = {
                        WellID: selectedWellId,
                        ReportDate: dateString,
                        CreatedBy: localStorage.getItem('username') || 'System'
                    };

                    const newReport = await apiFetch('/api/ddr/simple', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(reportPayload)
                    });

                    setCurrentReportId(newReport.ReportID);
                    setCurrentReport(newReport);
                    setReportDate(dateString);
                    setIsNewReport(true);
                    setSectionData({});
                    setEditingData({});
                    setEditingSections({});
                    setEditAttempts({});

                    // Reload reports list
                    const updatedReports = await apiFetch(`/api/ddr?wellId=${selectedWellId}`);
                    setReports(Array.isArray(updatedReports) ? updatedReports : []);

                    navigate(`/ddr?wellId=${selectedWellId}&reportId=${newReport.ReportID}`, { replace: true });
                    
                    await loadAllSectionData(selectedWellId, newReport.ReportID, dateString);
                    
                    setToast(`✅ New report created for ${formatDate(date)}`);
                    setShowCalendar(false);
                } catch (err) {
                    console.error('Failed to create new report:', err);
                    setToast(`❌ Failed to create report: ${err.message}`);
                } finally {
                    setIsCreatingReport(false);
                }
            } else {
                setShowCalendar(false);
            }
        }
    }, [exitAllEditModes, reports, selectedWellId, loadAllSectionData, navigate, formatDate]);

    const handleCreateNewReport = useCallback(async () => {
        if (!selectedWellId) {
            setToast('Please select a well first');
            return;
        }

        setIsCreatingReport(true);
        try {
            const reportPayload = {
                WellID: selectedWellId,
                ReportDate: newReportDate,
                CreatedBy: localStorage.getItem('username') || 'System'
            };

            const newReport = await apiFetch('/api/ddr/simple', {
                method: 'POST',
                body: JSON.stringify(reportPayload)
            });

            setCurrentReportId(newReport.ReportID);
            setCurrentReport(newReport);
            setReportDate(newReportDate);
            setIsNewReport(true);
            setSectionData({});
            setEditingData({});
            setEditingSections({});
            setEditAttempts({}); // Clear edit attempts for new report

            const updatedReports = await apiFetch(`/api/ddr?wellId=${selectedWellId}`);
            setReports(Array.isArray(updatedReports) ? updatedReports : []);

            navigate(`/ddr?wellId=${selectedWellId}&reportId=${newReport.ReportID}`, { replace: true });
            
            await loadAllSectionData(selectedWellId, newReport.ReportID, newReportDate);
            
            setToast(`✅ New report created for ${formatDate(newReportDate)}`);
            setShowNewReportModal(false);
        } catch (err) {
            console.error('Failed to create new report:', err);
            setToast('Failed to create new report');
        } finally {
            setIsCreatingReport(false);
        }
    }, [selectedWellId, newReportDate, navigate, loadAllSectionData]);

    const handleDeleteReport = useCallback(async () => {
        if (!currentReportId || !currentReport) {
            setToast('No report selected to delete');
            return;
        }

        setIsDeletingReport(true);
        try {
            // Delete the report
            await apiFetch(`/api/ddr/${currentReportId}`, {
                method: 'DELETE'
            });

            // Clear current report data
            setCurrentReport(null);
            setCurrentReportId(null);
            setSectionData({});
            setEditingData({});
            setEditingSections({});
            setEditAttempts({});

            // Reload reports list
            const updatedReports = await apiFetch(`/api/ddr?wellId=${selectedWellId}`);
            const sortedReports = Array.isArray(updatedReports) 
                ? updatedReports.sort((a, b) => new Date(b.ReportDate) - new Date(a.ReportDate))
                : [];
            
            setReports(sortedReports);

            // Navigate to the most recent report or clear URL if no reports left
            if (sortedReports.length > 0) {
                const latestReport = sortedReports[0];
                setCurrentReport(latestReport);
                setCurrentReportId(latestReport.ReportID);
                setReportDate(latestReport.ReportDate.split('T')[0]);
                
                await loadAllSectionData(selectedWellId, latestReport.ReportID, latestReport.ReportDate.split('T')[0]);
                navigate(`/ddr?wellId=${selectedWellId}&reportId=${latestReport.ReportID}`, { replace: true });
            } else {
                navigate(`/ddr?wellId=${selectedWellId}`, { replace: true });
            }

            setToast(`✅ Report for ${formatDate(currentReport.ReportDate)} deleted successfully`);
            setShowDeleteModal(false);
        } catch (err) {
            console.error('Failed to delete report:', err);
            setToast(`❌ Failed to delete report: ${err.message}`);
        } finally {
            setIsDeletingReport(false);
        }
    }, [currentReportId, currentReport, selectedWellId, navigate, loadAllSectionData]);

    // Export functions
    const handleExportPDF = useCallback(async () => {
        // Clear any previous export errors
        clearExportError();

        // Validate export readiness
        const ddrState = {
            wells,
            selectedWellId,
            currentReport,
            layoutConfig,
            sectionData,
            globalSettings,
            processedHeaderHTML
        };

        const validation = validateExportReadiness(ddrState);
        if (!validation.isReady) {
            setToast(`❌ Cannot export: ${validation.issues.join(', ')}`);
            return;
        }

        try {
            setToast('📄 Exporting PDF...');
            
            await quickExportToPDF(ddrState);
            
            setToast('✅ PDF exported successfully');
            
        } catch (error) {
            console.error('PDF export failed:', error);
            setToast(`❌ Failed to export PDF: ${error.message}`);
        }
    }, [
        wells, 
        selectedWellId, 
        currentReport, 
        sectionData, 
        layoutConfig, 
        globalSettings, 
        processedHeaderHTML,
        quickExportToPDF,
        validateExportReadiness,
        clearExportError
    ]);

    const handleExportExcel = useCallback(async () => {
        const selectedWell = wells.find(w => w.WellID === parseInt(selectedWellId));
        if (!selectedWell || !currentReport) {
            setToast('Please select a well and report first');
            return;
        }

        try {
            await exportDDRToExcel({
                well: selectedWell,
                currentReport,
                sectionData,
                layoutConfig,
                globalSettings
            });
            setToast('✅ Excel exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            setToast('❌ Failed to export Excel');
        }
    }, [wells, selectedWellId, currentReport, sectionData, layoutConfig, globalSettings]);

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [wellsResponse, layoutResponse, globalSettingsResponse] = await Promise.all([
                    apiFetch('/api/wells'),
                    apiFetch('/api/ddr-layout/config'),
                    getGlobalUnitSettings()
                ]);

                const wellsData = Array.isArray(wellsResponse) ? wellsResponse : [];
                setWells(wellsData);
                setLayoutConfig(layoutResponse);
                setGlobalSettings(globalSettingsResponse || {});
                
                // Debug: Check for calculated fields in layout config
                console.log('🔧 Layout config loaded:', {
                    sections: layoutResponse?.sections?.length || 0,
                    calculatedFieldsFound: layoutResponse?.sections?.reduce((acc, section) => {
                        const calculatedFields = section.fields?.filter(f => f.calculation || f.calculationType) || [];
                        if (calculatedFields.length > 0) {
                            acc[section.id] = calculatedFields.map(f => ({
                                name: f.name,
                                dbColumn: f.dbColumn,
                                calculation: f.calculation,
                                calculationType: f.calculationType
                            }));
                        }
                        return acc;
                    }, {}) || {}
                });
            } catch (err) {
                console.error('Failed to load initial data:', err);
                setToast('Failed to load initial data');
            }
        };

        loadInitialData();
    }, []);

    // Handle URL parameters
    useEffect(() => {
        if (!initialWellLoaded.current && wells.length > 0 && layoutConfig) {
            const urlWellId = params.get('wellId');
            if (urlWellId) {
                const wellExists = wells.some(w => w.WellID.toString() === urlWellId);
                if (wellExists) {
                    handleWellChange(urlWellId);
                    initialWellLoaded.current = true;
                }
            }
        }
    }, [wells, layoutConfig, params, handleWellChange]);

    const clearToast = useCallback(() => setToast(''), []);

    const orderedSections = useMemo(() => {
        if (!layoutConfig) return [];
        return layoutConfig.sections
            .filter(section => section.enabled)
            .sort((a, b) => a.order - b.order);
    }, [layoutConfig]);

    // Section navigation functions
    const toggleSection = useCallback((sectionId) => {
        const willBeCollapsed = !collapsedSections[sectionId];
        
        // If collapsing a section, exit edit mode for that specific section
        if (willBeCollapsed && editingSections[sectionId]) {
            console.log('🚫 Exiting edit mode for collapsed section:', sectionId);
            setEditingSections(prev => ({ ...prev, [sectionId]: false }));
            setEditingData(prev => {
                const newData = { ...prev };
                delete newData[sectionId];
                return newData;
            });
            setToast(`🚫 Edit mode exited due to section collapse`);
        }
        
        setCollapsedSections(prev => ({
            ...prev,
            [sectionId]: willBeCollapsed
        }));
    }, [collapsedSections, editingSections]);

    const toggleAllSections = useCallback(() => {
        const allSections = orderedSections.map(section => section.id);
        const allCollapsed = allSections.every(sectionId => collapsedSections[sectionId]);

        console.log('🔄 Toggle all sections:', { allSections, allCollapsed, currentCollapsed: collapsedSections });

        if (allCollapsed) {
            // Expand all
            setCollapsedSections({});
        } else {
            // Collapse all
            const newCollapsed = {};
            allSections.forEach(sectionId => {
                newCollapsed[sectionId] = true;
            });
            setCollapsedSections(newCollapsed);
        }
    }, [orderedSections, collapsedSections]);

    const jumpToSection = useCallback((sectionId) => {
        const element = sectionRefs.current[sectionId]?.current;
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    // Generate global styles from layout configuration
    const globalStyles = useMemo(() => generateGlobalStyles(layoutConfig), [layoutConfig]);

    // Generate custom CSS from layout configuration
    const customCSS = useMemo(() => generateCustomCSS(layoutConfig), [layoutConfig]);

    // Initialize section refs when orderedSections changes
    useEffect(() => {
        orderedSections.forEach(section => {
            if (!sectionRefs.current[section.id]) {
                sectionRefs.current[section.id] = React.createRef();
            }
        });
    }, [orderedSections]);

    // Process header HTML when dependencies change
    useEffect(() => {
        const updateHeader = async () => {
            if (layoutConfig && wells.length > 0 && selectedWellId) {
                const headerHTML = await processHeaderHTML(
                    layoutConfig, 
                    wells, 
                    selectedWellId, 
                    reportDate, 
                    currentReport, 
                    isNewReport
                );
                setProcessedHeaderHTML(headerHTML);
            }
        };
        updateHeader();
    }, [layoutConfig, wells, selectedWellId, reportDate, currentReport, isNewReport]);

    // Render configurable header
    const renderHeader = () => {
        if (!processedHeaderHTML) return null;
        return <div dangerouslySetInnerHTML={{ __html: processedHeaderHTML }} />;
    };

    return (
        <div className="ddr-v2-container" style={globalStyles}>
            {/* Apply custom CSS from layout configuration */}
            {customCSS && (
                <style dangerouslySetInnerHTML={{ __html: customCSS }} />
            )}
            
            {/* Minimal Single Row Controls */}
            <div className={`ddr-controls-minimal ${isControlsMinimized ? 'collapsed' : 'expanded'}`}>
                {/* Always Visible Minimal Controls */}
                <div className="controls-minimal-row">
                    {/* Well Selection */}
                    <div className="control-group-minimal">
                        <label>Well:</label>
                        <select
                            value={selectedWellId}
                            onChange={(e) => handleWellChange(e.target.value)}
                            disabled={isLoading}
                            className="select-minimal"
                        >
                            <option value="">Select Well...</option>
                            {wells.map(well => (
                                <option key={well.WellID} value={well.WellID}>
                                    {well.WellName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Date Selection */}
                    <div className="control-group-minimal" ref={calendarRef}>
                        <label>Date:</label>
                        <div className="date-input-minimal">
                            <select
                                value={currentReport?.ReportID || ''}
                                onChange={(e) => {
                                    const reportId = e.target.value;
                                    const report = reports.find(r => r.ReportID === parseInt(reportId));
                                    if (report) {
                                        handleDateSelect(new Date(report.ReportDate));
                                    }
                                }}
                                disabled={isLoading}
                                className="select-minimal"
                            >
                                <option value="">Select Date...</option>
                                {reports.map(report => (
                                    <option key={report.ReportID} value={report.ReportID}>
                                        {formatDate(report.ReportDate)}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn-minimal calendar-btn"
                                onClick={() => setShowCalendar(!showCalendar)}
                                title="Calendar"
                                type="button"
                                disabled={isLoading || isCreatingReport}
                            >
                                {isCreatingReport ? '⏳' : '📅'}
                            </button>
                            
                            {/* New Report Button */}
                            {selectedWellId && (role === 'admin' || role === 'read-write') && (
                                <button
                                    className="btn-minimal new-btn"
                                    onClick={() => setShowNewReportModal(true)}
                                    title="New Report"
                                >
                                    + New Report
                                </button>
                            )}
                        </div>
                        
                        {showCalendar && (
                            <div className="calendar-popup">
                                <div className="calendar-header">
                                    <h4>Select Report Date</h4>
                                    <button 
                                        className="calendar-close-btn"
                                        onClick={() => setShowCalendar(false)}
                                        type="button"
                                        disabled={isCreatingReport}
                                    >
                                        ✕
                                    </button>
                                </div>
                                {isCreatingReport ? (
                                    <div className="calendar-loading">
                                        <div className="loading-spinner">⏳</div>
                                        <p>Creating report...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="calendar-instructions">
                                            <p>📊 <strong>Blue dates</strong> have existing reports</p>
                                            <p>📝 Click any date to view or create a report</p>
                                        </div>
                                        <DatePicker
                                            inline
                                            selected={reportDate ? new Date(reportDate) : null}
                                            onChange={handleDateSelect}
                                            highlightDates={reports.map(r => new Date(r.ReportDate))}
                                            dayClassName={date => {
                                                const hasReport = reports.some(r => isSameDate(r.ReportDate, date));
                                                return hasReport ? 'has-report' : undefined;
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="control-group-minimal actions-minimal">
                        {/* Navigation Toggle Button */}
                        <button 
                            className="btn-minimal navigation-toggle-btn"
                            onClick={() => setIsControlsMinimized(!isControlsMinimized)}
                            title={isControlsMinimized ? "Show Section Navigation" : "Hide Section Navigation"}
                        >
                            {isControlsMinimized ? '📋' : '🔽'}
                        </button>


                        
                        {currentReport && (role === 'admin' || role === 'read-write') && (
                            <button
                                className="btn-minimal delete-btn"
                                onClick={() => setShowDeleteModal(true)}
                                title="Delete Report"
                            >
                                🗑️
                            </button>
                        )}
                        
                        {currentReport && (
                            <>
                                <button 
                                    onClick={handleExportPDF} 
                                    className={`btn-minimal export-btn ${isExporting ? 'exporting' : ''}`}
                                    title={isExporting ? "Exporting PDF..." : "Export PDF"}
                                    disabled={isExporting}
                                >
                                    {isExporting ? '⏳' : '📄'}
                                </button>
                                <button 
                                    onClick={handleExportExcel} 
                                    className="btn-minimal export-btn"
                                    title="Export Excel"
                                >
                                    📊
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Section Navigation - Only when expanded */}
                {!isControlsMinimized && selectedWellId && layoutConfig && orderedSections.length > 0 && (
                    <div className="section-navigation-minimal">
                        <div className="section-nav-header">
                            <span>📋 Section Navigation</span>
                            <button 
                                className="btn-minimal toggle-all-btn"
                                onClick={toggleAllSections}
                                title="Expand/Collapse All Sections"
                            >
                                {(() => {
                                    const sectionIds = orderedSections.map(s => s.id);
                                    if (sectionIds.length === 0) return 'Expand All';
                                    const allCollapsed = sectionIds.every(sectionId => collapsedSections[sectionId]);
                                    const noneCollapsed = sectionIds.every(sectionId => !collapsedSections[sectionId]);
                                    if (allCollapsed) return 'Expand All';
                                    if (noneCollapsed) return 'Collapse All';
                                    return 'Collapse All';
                                })()}
                            </button>
                        </div>
                        <div className="section-nav-buttons-minimal">
                            {orderedSections.map(section => (
                                <button 
                                    key={section.id}
                                    className={`btn-minimal section-nav-btn ${collapsedSections[section.id] ? 'collapsed' : 'expanded'}`}
                                    onClick={() => {
                                        toggleSection(section.id);
                                        if (collapsedSections[section.id]) {
                                            setTimeout(() => jumpToSection(section.id), 100);
                                        }
                                    }}
                                    title={`${collapsedSections[section.id] ? 'Expand' : 'Collapse'} ${section.name}`}
                                >
                                    {collapsedSections[section.id] ? '▶' : '▼'} {section.name.replace(/\s*\([^)]*\)/g, '').trim()}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="ddr-content">
                {isLoading ? (
                    <div className="loading-indicator">Loading...</div>
                ) : currentReport || isNewReport ? (
                    <div className="report-container">
                        {/* Configurable Header */}
                        {renderHeader()}
                        
                        <div className="sections-container">
                            {orderedSections.map(sectionConfig => (
                                <Section
                                    key={sectionConfig.id}
                                    config={sectionConfig}
                                    data={sectionData[sectionConfig.id]}
                                    isEditing={editingSections[sectionConfig.id]}
                                    editingData={editingData[sectionConfig.id]}
                                    onEdit={handleEditSection}
                                    onEditForm={handleEditForm}
                                    onSave={saveSectionData}
                                    onCancel={handleCancelEdit}
                                    onFieldChange={handleFieldChange}
                                    onMultiFieldChange={handleMultiFieldChange}
                                    onAddRow={handleAddRow}
                                    onRemoveRow={handleRemoveRow}
                                    onCopyFromLastDDR={handleCopyFromLastDDR}
                                    onToast={setToast}
                                    globalSettings={globalSettings}
                                    role={role}
                                    isSaving={savingStates[sectionConfig.id]}
                                    isCollapsed={collapsedSections[sectionConfig.id]}
                                    onToggleCollapse={toggleSection}
                                    sectionRef={sectionRefs.current[sectionConfig.id]}
                                    onContextMenu={handleGlobalContextMenu}
                                    selectedWellId={selectedWellId}
                                    reportDate={reportDate}
                                    currentReportId={currentReportId}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="no-report-selected">
                        <p>Select a well and date to view a report, or create a new one.</p>
                    </div>
                )}
            </div>

            {/* New Report Modal */}
            {showNewReportModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Create New Report</h3>
                        <div className="modal-body">
                            <label>Report Date:</label>
                            <input
                                type="date"
                                value={newReportDate}
                                onChange={(e) => setNewReportDate(e.target.value)}
                            />
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={() => setShowNewReportModal(false)}
                                disabled={isCreatingReport}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateNewReport}
                                disabled={isCreatingReport}
                            >
                                {isCreatingReport ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Report Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 style={{ color: '#ef4444' }}>⚠️ Delete Report</h3>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this report?</p>
                            <div style={{ 
                                background: '#fef2f2', 
                                border: '1px solid #fecaca', 
                                borderRadius: '6px', 
                                padding: '12px', 
                                margin: '16px 0' 
                            }}>
                                <strong>Report Details:</strong>
                                <br />
                                <strong>Date:</strong> {currentReport ? formatDate(currentReport.ReportDate) : 'N/A'}
                                <br />
                                <strong>Well:</strong> {wells.find(w => w.WellID === parseInt(selectedWellId))?.WellName || 'N/A'}
                            </div>
                            <p style={{ color: '#dc2626', fontSize: '14px' }}>
                                <strong>Warning:</strong> This action cannot be undone. All data associated with this report will be permanently deleted.
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeletingReport}
                                style={{
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteReport}
                                disabled={isDeletingReport}
                                style={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    marginLeft: '8px'
                                }}
                            >
                                {isDeletingReport ? '🗑️ Deleting...' : '🗑️ Delete Report'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DataForm Modal */}
            {showDataForm && dataFormConfig && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ 
                        maxWidth: dataFormConfig.viewMode === 'table' ? '95%' : '800px', 
                        width: '90%',
                        maxHeight: '90vh'
                    }}>
                        <h3>
                            📝 Edit {dataFormConfig.sectionConfig?.name} - {dataFormConfig.viewMode === 'table' ? 'Table View' : 'Form View'}
                        </h3>
                        <p>
                            Table: {dataFormConfig.tableName} | 
                            Mode: {dataFormConfig.viewMode === 'table' ? 'Table Editing' : 'Form Editing'} |
                            Records: {dataFormConfig.viewMode === 'table' ? dataFormConfig.tableData?.length || 0 : '1'}
                        </p>
                        
                        <DataForm
                            key={`${dataFormConfig.sectionConfig?.id}-${dataFormConfig.viewMode}-${Date.now()}`}
                            // Standard props
                            data={dataFormConfig.data}
                            tableName={dataFormConfig.tableName}
                            onSave={handleDataFormSave}
                            onCancel={handleDataFormCancel}
                            title={`Edit ${dataFormConfig.sectionConfig?.name || 'Data'}`}
                            skipAutoSave={true}
                            // Table view props
                            viewMode={dataFormConfig.viewMode}
                            sectionConfig={dataFormConfig.sectionConfig}
                            tableData={dataFormConfig.tableData || []}
                            // Context props for custom calculations
                            selectedWellId={selectedWellId}
                            reportDate={reportDate}
                            currentReportId={currentReportId}
                        />
                    </div>
                </div>
            )}

            {/* Hidden PDF Content Renderer */}
            {createPDFContentRenderer && 
             wells.length > 0 && 
             selectedWellId && 
             currentReport && 
             layoutConfig && (
                createPDFContentRenderer({
                    well: wells.find(w => w.WellID?.toString() === selectedWellId?.toString()),
                    currentReport,
                    sectionData,
                    layoutConfig,
                    globalSettings,
                    processedHeaderHTML
                })
            )}

            {/* Toast Notification */}
            <Toast message={toast} onClose={clearToast} />

            {/* Global Context Menu */}
            <ContextMenu
                x={globalContextMenu.x}
                y={globalContextMenu.y}
                isVisible={globalContextMenu.visible}
                onClose={handleCloseGlobalContextMenu}
                options={globalContextMenuOptions}
            />
        </div>
    );
};

export default DDR_v2; 